import { useMutation } from '@tanstack/react-query';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Dropdown,
  Form,
  Image,
  Input,
  InputNumber,
  MenuProps,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import { CustomerApis } from 'apis/CustomerApis';
import { EinvoiceHistoryApis } from 'apis/EinvoiceHistoryApis';
import { PaymentSummaryV2Apis } from 'apis/PaymentSummaryV2Apis';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import path from 'routes/Path';
import formatNumber, {
  formatCurrency,
  formatDate,
} from 'utils/constants/functions';
import {
  CaretDownOutlined,
  CaretUpOutlined,
  CopyOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileAddOutlined,
  FileTextOutlined,
  HistoryOutlined,
  MailOutlined,
  MessageOutlined,
  MoreOutlined,
  PrinterOutlined,
  SendOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import PaymentSummaryEinvoiceHistoryModal from './PaymentSummaryEinvoiceHistoryModal';

const { Text } = Typography;

const DetailPaymentSummaryPageV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { setTitle, setBreadcrumb } = useTitle();
  const [form] = useForm();

  // Route params from URL search query or location state
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const queryId =
    queryParams.get('id') || queryParams.get('paySummaryId') || '';
  const stateParams = (location.state as any) || {};
  const initialSumPaymentId =
    queryId ||
    stateParams.sumPaymentId ||
    stateParams.id ||
    stateParams.paySummaryId ||
    '';

  const [sumPaymentId, setSumPaymentId] = useState<string>(initialSumPaymentId);
  const isAddState = !sumPaymentId && stateParams.isAdd !== false;
  const [customerIdSelected, setCustomerIdSelected] = useState<string>(
    stateParams.customerId || '',
  );
  const [teamIdSelected, setTeamIdSelected] = useState<string>(
    stateParams.teamId || '',
  );

  // Master Data & UI State
  const [customerData, setCustomersData] =
    useState<ILazySelectData>(initLazySelectData);
  const [detailData, setDetailData] = useState<any>({});
  const [saleOrderList, setSaleOrderList] = useState<any[]>([]);
  const [purchaseOrderList, setPurchaseOrderList] = useState<any[]>([]);
  const [sapDebtItems, setSapDebtItems] = useState<any[]>([]);
  const [qrList, setQrList] = useState<any[]>([]);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [splitAmountInput, setSplitAmountInput] = useState<number>(0);
  const [selectedQrDetail, setSelectedQrDetail] = useState<any>(null);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState<boolean>(false);

  // Payment Details Modal State
  const [isPaymentDetailsModalOpen, setIsPaymentDetailsModalOpen] =
    useState<boolean>(false);
  const [paymentDetailsLines, setPaymentDetailsLines] = useState<any[]>([]);
  const [isFullyPaid, setIsFullyPaid] = useState<boolean>(false);
  const [isDetailLoading, setIsDetailLoading] = useState<boolean>(false);
  const [paymentDetailsLoading, setPaymentDetailsLoading] =
    useState<boolean>(false);
  const [sendingSapFull, setSendingSapFull] = useState<boolean>(false);

  // HDDT State
  const [isHddtModalOpen, setIsHddtModalOpen] = useState<boolean>(false);
  const [hddtOrders, setHddtOrders] = useState<any[]>([]);
  const [exportHddtLoading, setExportHddtLoading] = useState<boolean>(false);
  const [isEinvoiceHistoryModalOpen, setIsEinvoiceHistoryModalOpen] =
    useState<boolean>(false);
  const [einvoiceSummary, setEinvoiceSummary] = useState<{
    total: number;
    success: number;
  }>({ total: 0, success: 0 });

  // Selected statement IDs
  const [selectedSaleOrders, setSelectedSaleOrders] = useState<string[]>([]);
  const [selectedPurchaseOrders, setSelectedPurchaseOrders] = useState<
    string[]
  >([]);

  // Panel Collapsible Expansion States
  const [isCongNoExpanded, setIsCongNoExpanded] = useState<boolean>(true);
  const [isBangKeExpanded, setIsBangKeExpanded] = useState<boolean>(true);
  const [isBangDeExpanded, setIsBangDeExpanded] = useState<boolean>(true);
  const [isTongCongExpanded, setIsTongCongExpanded] = useState<boolean>(true);

  // Modals state
  const [isSelectSalesModalOpen, setIsSelectSalesModalOpen] = useState(false);
  const [isSelectPurchasesModalOpen, setIsSelectPurchasesModalOpen] =
    useState(false);
  const [tempSelectedSales, setTempSelectedSales] = useState<string[]>([]);
  const [tempSelectedPurchases, setTempSelectedPurchases] = useState<string[]>(
    [],
  );

  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [isQrModalLoading, setIsQrModalLoading] = useState(false);
  const [isCheckingAllQrStatus, setIsCheckingAllQrStatus] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [qrAmount, setQrAmount] = useState<number>(0);
  const [generatedQrs, setGeneratedQrs] = useState<any[]>([]);
  const activeQrs = useMemo(
    () =>
      generatedQrs.filter(
        (qr: any) => (qr.status || '').toString().toUpperCase() !== 'CANCELLED',
      ),
    [generatedQrs],
  );

  const hasPaidQr = useMemo(
    () =>
      activeQrs.some((qr: any) => {
        const statusStr = (qr.status || 'PENDING').toString().toUpperCase();
        return statusStr === 'SUCCESS' || statusStr === 'PAID';
      }),
    [activeQrs],
  );

  const cancelledQrs = useMemo(
    () =>
      generatedQrs.filter(
        (qr: any) => (qr.status || '').toString().toUpperCase() === 'CANCELLED',
      ),
    [generatedQrs],
  );
  const [qrNote, setQrNote] = useState<string>('');
  const [thucTeTransfer, setThucTeTransfer] = useState<number>(0);
  const [isUserEditedTransfer, setIsUserEditedTransfer] =
    useState<boolean>(false);

  // Set page title & global header breadcrumb (no duplicate breadcrumb)
  useEffect(() => {
    setTitle('Bảng Tổng hợp thanh toán');
    setBreadcrumb([
      { title: 'Kế toán/Thủ quỹ' },
      { title: 'Bảng Tổng hợp thanh toán', href: path.PAYMENT_SUMMARY_V2 },
      { title: isAddState ? 'Tạo mới' : sumPaymentId || 'Chi tiết' },
    ]);
  }, [setTitle, setBreadcrumb, isAddState, sumPaymentId]);

  // Check if all selected sale orders have billing ID (Inspects saleOrderList, sapDebtItems, and paymentDetailsLines)
  const allSelectedOrdersHaveBilling = useMemo(() => {
    if (selectedSaleOrders.length === 0) return true;
    return selectedSaleOrders.every((id) => {
      // 1. Check in paymentDetailsLines
      const inDetails = paymentDetailsLines.find(
        (line: any) =>
          line.orderId === id ||
          line.order_id === id ||
          line.billingCode === id ||
          line.bill === id,
      );
      if (inDetails) {
        const b = (inDetails.billingCode || inDetails.bill || '').trim();
        if (b && b !== '-') return true;
      }

      // 2. Check in saleOrderList
      const matched = saleOrderList.find(
        (o: any) => o.orderId === id || o.order_id === id || o.id === id,
      );
      if (matched) {
        // Skip check for Debt (Dẻ / Công nợ cũ) and Purchase (Mua lại)
        const rawType = matched.type || matched.orderType || '';
        const typeStr = String(rawType).toUpperCase();
        if (typeStr === 'DEBT' || typeStr === 'PURCHASE') {
          return true;
        }
        const billing =
          matched.billingId ||
          matched.billing_id ||
          matched.billing ||
          matched.billingCode ||
          matched.billing_code ||
          matched.billCode ||
          matched.bill_code ||
          matched.billingNo ||
          matched.billing_no ||
          matched.billNo ||
          matched.bill_no;
        const bStr = String(billing || '').trim();
        if (bStr !== '' && bStr !== '-') {
          return true;
        }
      }

      // 3. Check in sapDebtItems
      const hasSapDebtBilling = sapDebtItems.some((item: any) => {
        const itemBill = (item.BILL || '').trim();
        if (!itemBill || itemBill === '-') return false;
        let matchedOrderId = '';
        if (matched) {
          matchedOrderId =
            matched.orderId || matched.order_id || matched.id || '';
        }
        return (
          item.VBELN === id ||
          item.VBELN === matchedOrderId ||
          item.BILL === id ||
          item.BILL === matchedOrderId
        );
      });
      if (hasSapDebtBilling) return true;

      return false;
    });
  }, [
    selectedSaleOrders,
    saleOrderList,
    sapDebtItems,
    paymentDetailsLines,
    sumPaymentId,
  ]);

  // Aggregate QR payment status
  const aggregatePaymentStatus = useMemo(() => {
    const active = qrList.filter(
      (qr) => (qr.status || '').toString().toUpperCase() !== 'CANCELLED',
    );
    if (active.length === 0) return 'Chưa Thanh Toán';
    const paidCount = active.filter((qr) => {
      const s = (qr.status || '').toString().toUpperCase();
      return s === 'SUCCESS' || s === 'PAID';
    }).length;
    const failureCount = active.filter(
      (qr) => (qr.status || '').toString().toUpperCase() === 'FAILURE',
    ).length;
    if (failureCount > 0) return 'Lỗi Thanh Toán';

    if (isFullyPaid || (paidCount > 0 && paidCount === active.length)) {
      return 'Đã Thanh Toán';
    }

    if (paymentDetailsLines.length > 0) {
      const hasUnpaidLine = paymentDetailsLines.some(
        (l: any) =>
          !l.isAutoOffset &&
          Number(l.totalAmount || 0) > 0 &&
          (l.paymentStatus === 'UNPAID' ||
            l.paymentStatus === 'Chưa thanh toán' ||
            l.status === 'Chưa thanh toán' ||
            l.paymentStatus === 'PARTIAL' ||
            l.paymentStatus === 'Thanh toán 1 phần' ||
            l.status === 'Thanh toán 1 phần'),
      );
      if (hasUnpaidLine && paidCount > 0) {
        return 'Thanh Toán Một Phần';
      }
    }

    if (paidCount > 0 && !isFullyPaid) {
      return 'Thanh Toán Một Phần';
    }

    if (paidCount === active.length) return 'Đã Thanh Toán';
    if (paidCount > 0) return 'Thanh Toán Một Phần';
    return 'Chưa Thanh Toán';
  }, [qrList, isFullyPaid, paymentDetailsLines]);

  const aggregatePaymentStatusColor = useMemo(() => {
    switch (aggregatePaymentStatus) {
      case 'Đã Thanh Toán':
        return 'success';
      case 'Thanh Toán Một Phần':
        return 'warning';
      case 'Lỗi Thanh Toán':
        return 'error';
      default:
        return 'default';
    }
  }, [aggregatePaymentStatus]);

  const hasActiveQr = useMemo(() => {
    if (qrList.length === 0) return false;
    return qrList.some(
      (qr) => (qr.status || '').toString().toUpperCase() !== 'CANCELLED',
    );
  }, [qrList]);

  // Query & merge active SAP debts with local database state
  const fetchAndMergeSapDebts = async (
    custId: string,
    teamId: string,
    savedDebtList: any[] = [],
  ) => {
    if (!custId) return;
    try {
      const activeDebtsRes = await PaymentSummaryV2Apis.getMobileSapDebtItems({
        customerId: custId,
        teamId: teamId || user?.teamId || user?.teamSaleId || user?.team_id,
        sumPaymentId: sumPaymentId || undefined,
      });

      const activeSapItems = activeDebtsRes.data || [];
      const merged: any[] = [];
      const savedMap = new Map<string, any>();
      savedDebtList.forEach((item) => {
        savedMap.set(item.BELNR?.toString() || '', item);
      });
      const matchedBelnrs = new Set<string>();

      activeSapItems.forEach((activeItem: any) => {
        const belnr = activeItem.BELNR?.toString() || '';
        if (savedMap.has(belnr)) {
          const savedItem = savedMap.get(belnr);
          merged.push({
            ...activeItem,
            VBELN: savedItem.VBELN || activeItem.VBELN || '-',
            THTT: savedItem.THTT || activeItem.THTT || '-',
            checked: true,
          });
          matchedBelnrs.add(belnr);
        } else {
          merged.push({
            ...activeItem,
            checked: false,
          });
        }
      });

      savedDebtList.forEach((savedItem) => {
        const belnr = savedItem.BELNR?.toString() || '';
        if (!matchedBelnrs.has(belnr)) {
          merged.push({
            ...savedItem,
            checked: true,
          });
        }
      });

      setSapDebtItems(merged);
    } catch (e) {
      console.log('Error fetchAndMergeSapDebts', e);
    }
  };

  // Load detail data in Edit mode
  const loadDetailData = async (paymentId: string) => {
    try {
      setIsDetailLoading(true);
      const res =
        await PaymentSummaryV2Apis.getMobileBillPaymentDetail(paymentId);
      if (res.code === 0 && res.data) {
        const payload = res.data;
        setDetailData({
          ...(payload.detail || {}),
          saleOrder: payload.saleOrder,
          purchaseOrder: payload.purchaseOrder,
        });
        setSaleOrderList(payload.saleOrder?.listSaleOrder || []);
        setPurchaseOrderList(payload.purchaseOrder?.listPurchaseOrder || []);
        setCustomerIdSelected(payload.detail?.customerId || '');
        setTeamIdSelected(payload.detail?.teamSaleId || '');

        const savedSaleChecked =
          payload.detail?.saleOrder && payload.detail.saleOrder.length > 0
            ? payload.detail.saleOrder
            : (payload.saleOrder?.listSaleOrder || [])
                .filter((x: any) => x.checked)
                .map((x: any) =>
                  typeof x === 'string'
                    ? x
                    : x.orderId || x.order_id || x.id || '',
                );

        const savedPurchaseChecked =
          payload.detail?.purchaseOrder &&
          payload.detail.purchaseOrder.length > 0
            ? payload.detail.purchaseOrder
            : (payload.purchaseOrder?.listPurchaseOrder || [])
                .filter((x: any) => x.checked)
                .map((x: any) =>
                  typeof x === 'string'
                    ? x
                    : x.orderId || x.order_id || x.id || '',
                );

        setSelectedSaleOrders(savedSaleChecked);
        setSelectedPurchaseOrders(savedPurchaseChecked);

        const savedTransfer =
          payload.detail?.codPrice || payload.detail?.transferPrice || 0;
        setThucTeTransfer(savedTransfer);
        if (savedTransfer > 0) {
          setIsUserEditedTransfer(true);
        }

        await fetchAndMergeSapDebts(
          payload.detail?.customerId,
          payload.detail?.teamSaleId,
          payload.sapDebt || [],
        );

        const qrRes = await PaymentSummaryV2Apis.getMobileQrCodes(paymentId);
        if (qrRes.code === 0) {
          setQrList(qrRes.data || []);
        }

        fetchPaymentDetails(paymentId);

        form.setFieldsValue({
          customerId: payload.detail?.customerId,
          teamSaleId: payload.detail?.teamSaleId,
          createDate: payload.detail?.createDate
            ? dayjs(payload.detail.createDate)
            : dayjs(),
          marketPrice: payload.detail?.marketPrice || 0,
          codPrice: payload.detail?.codPrice || 0,
          transferPrice: payload.detail?.transferPrice || 0,
          purchaseDeductOrder: payload.detail?.purchaseDeductOrder || 0,
          customerDebitAmount: payload.detail?.customerDebitAmount || 0,
          voucher: payload.detail?.voucher || 0,
          totalNote: payload.detail?.totalNote || '',
          note: payload.detail?.note || '',
        });

        // Tự động gọi API recalculate khi load chi tiết/reload trang để load danh sách dropdown
        if (payload.detail?.customerId) {
          const body = {
            customerId: payload.detail.customerId,
            teamSaleId:
              payload.detail.teamSaleId || user?.teamSales?.[0]?.id || '',
            marketPrice: payload.detail.marketPrice || 0,
            saleOrder: savedSaleChecked,
            purchaseOrder: savedPurchaseChecked,
            id: paymentId,
          };
          const recalcRes =
            await PaymentSummaryV2Apis.recalculateBillPayment(body);
          if (recalcRes.code === 0 && recalcRes.data) {
            setSaleOrderList(recalcRes.data.saleOrder?.listSaleOrder || []);
            setPurchaseOrderList(
              recalcRes.data.purchaseOrder?.listPurchaseOrder || [],
            );
          }
        }
      }
    } catch (e: any) {
      message.error(e.message || 'Lỗi khi tải chi tiết bảng kê');
    } finally {
      setIsDetailLoading(false);
    }
  };

  useEffect(() => {
    if (initialSumPaymentId && initialSumPaymentId !== sumPaymentId) {
      setSumPaymentId(initialSumPaymentId);
    }
  }, [initialSumPaymentId]);

  const fetchEinvoiceSummary = async (paymentId: string) => {
    if (!paymentId) return;
    try {
      const res: any = await EinvoiceHistoryApis.getEinvoiceSendHistory({
        sumPaymentId: paymentId,
        limit: 100,
        page: 1,
      });
      const items = res?.items || res?.data?.items || res?.data || [];
      if (Array.isArray(items) && items.length > 0) {
        const total = items.length;
        const success = items.filter(
          (item: any) =>
            item.emailStatus?.status === 'SUCCESS' ||
            item.smsStatus?.status === 'SUCCESS',
        ).length;
        setEinvoiceSummary({ total, success });
      } else {
        setEinvoiceSummary({ total: 0, success: 0 });
      }
    } catch {
      // Ignore background summary load error
    }
  };

  useEffect(() => {
    if (!isAddState && sumPaymentId) {
      loadDetailData(sumPaymentId);
      fetchEinvoiceSummary(sumPaymentId);
    } else {
      form.setFieldsValue({
        createDate: dayjs(),
        marketPrice: 0,
        codPrice: 0,
        transferPrice: 0,
        purchaseDeductOrder: 0,
        customerDebitAmount: 0,
        voucher: 0,
      });
    }
  }, [sumPaymentId, isAddState]);

  // Mutation: Get Customers in Combobox
  const { mutate: getCustomersMutate, isPending: isGetCustomersPending } =
    useMutation({
      mutationFn: (params: {
        pageSize: number;
        searchValue: string;
        plantId?: string;
      }) => CustomerApis.getCustomersInCombobox(params),
      onSuccess: (res) => {
        if (res?.data?.getCustomer) {
          const data = res?.data?.getCustomer;
          setCustomersData({
            options: data.items.map((item: any) => ({
              label: `${item.sapCustomerId} - ${item.storeName || item.name}`,
              value: item.id,
              customer: item,
            })),
            totalItems: data?.pageInfo?.totalItems,
          });
        }
      },
    });

  const handleCustomerFetch = (params: ILazySelectParams) => {
    getCustomersMutate({
      pageSize: params.pageSize,
      searchValue: params.value || '',
      plantId: user?.plantCode,
    });
  };

  const fetchAvailableStatementsAndRecalculate = async (
    custId: string,
    teamId: string,
    salesSelected: string[],
    purchasesSelected: string[],
    rate: number,
    shouldReloadDebts = false,
  ) => {
    if (!custId) return;
    try {
      const body = {
        customerId: custId,
        teamSaleId: teamId || user?.teamSales?.[0]?.id || '',
        marketPrice: rate,
        saleOrder: salesSelected,
        purchaseOrder: purchasesSelected,
        id: sumPaymentId || undefined,
      };
      const res = await PaymentSummaryV2Apis.recalculateBillPayment(body);
      if (res.code === 0 && res.data) {
        const payload = res.data;
        setDetailData((prev: any) => ({
          ...prev,
          ...(payload.detail || {}),
          saleOrder: payload.saleOrder || prev?.saleOrder,
          purchaseOrder: payload.purchaseOrder || prev?.purchaseOrder,
        }));
        setSaleOrderList(payload.saleOrder?.listSaleOrder || []);
        setPurchaseOrderList(payload.purchaseOrder?.listPurchaseOrder || []);

        if (rate === 0 && payload.detail?.marketPrice) {
          form.setFieldsValue({ marketPrice: payload.detail.marketPrice });
        }

        // Chỉ khi khách hàng thay đổi mới reload lại công nợ từ SAP
        if (shouldReloadDebts) {
          await fetchAndMergeSapDebts(custId, teamId, []);
        }
      }
    } catch (e: any) {
      message.error(e.message || 'Lỗi khi tính toán lại đơn hàng');
    }
  };

  const handleCustomerChange = async (val: string, option: any) => {
    setCustomerIdSelected(val);
    setSelectedSaleOrders([]);
    setSelectedPurchaseOrders([]);
    setSaleOrderList([]);
    setPurchaseOrderList([]);
    setSapDebtItems([]);

    const customerObj = option?.customer;
    const primaryTeam =
      customerObj?.tteamSales?.length > 0
        ? customerObj.tteamSales[0].id
        : user?.teamSales?.[0]?.id || '';

    form.setFieldsValue({ teamSaleId: primaryTeam });
    setTeamIdSelected(primaryTeam);
    await fetchAvailableStatementsAndRecalculate(
      val,
      primaryTeam,
      [],
      [],
      0,
      true,
    );
  };

  // Visible SAP debts in Panel 1 & Sidebar Checklist
  const visibleSapDebts = useMemo(() => {
    return sapDebtItems.filter((item) => {
      if (item.isAutoOffset || item.is_auto_offset) return false;
      const billing = item.BILL || '';
      const miro = item.MIRO || '';
      const vbeln = item.VBELN || '';

      let isSelectedInStatements = false;
      if (vbeln && vbeln !== '-') {
        if (
          selectedSaleOrders.includes(vbeln) ||
          selectedPurchaseOrders.includes(vbeln)
        ) {
          isSelectedInStatements = true;
        }
      }
      if (billing && billing !== '-') {
        if (selectedSaleOrders.includes(billing)) {
          isSelectedInStatements = true;
        }
      }
      if (miro && miro !== '-') {
        if (selectedPurchaseOrders.includes(miro)) {
          isSelectedInStatements = true;
        }
      }
      return !isSelectedInStatements;
    });
  }, [sapDebtItems, selectedSaleOrders, selectedPurchaseOrders]);

  // Checked SAP debt items
  const checkedSapDebts = useMemo(() => {
    return visibleSapDebts.filter((x) => x.checked);
  }, [visibleSapDebts]);

  // SAP debt sums
  const totalSapDebtVal = useMemo(() => {
    return visibleSapDebts.reduce(
      (sum, x) => sum + (Number(x.DMBTR) || Number(x.paidAmount) || 0),
      0,
    );
  }, [visibleSapDebts]);

  const totalCheckedSapDebtVal = useMemo(() => {
    return checkedSapDebts.reduce(
      (sum, x) => sum + (Number(x.DMBTR) || Number(x.paidAmount) || 0),
      0,
    );
  }, [checkedSapDebts]);

  // Matched SAP debts debug card for Panel 2
  const matchedDebts = useMemo(() => {
    return sapDebtItems.filter((item) => {
      const billing = item.BILL || '';
      const miro = item.MIRO || '';
      const vbeln = item.VBELN || '';

      let isSelectedInStatements = false;
      if (vbeln && vbeln !== '-') {
        if (
          selectedSaleOrders.includes(vbeln) ||
          selectedPurchaseOrders.includes(vbeln)
        ) {
          isSelectedInStatements = true;
        }
      }
      if (billing && billing !== '-') {
        if (selectedSaleOrders.includes(billing)) {
          isSelectedInStatements = true;
        }
      }
      if (miro && miro !== '-') {
        if (selectedPurchaseOrders.includes(miro)) {
          isSelectedInStatements = true;
        }
      }
      return isSelectedInStatements;
    });
  }, [sapDebtItems, selectedSaleOrders, selectedPurchaseOrders]);

  // Table totals calculations (matching Mobile V2)
  const t2 =
    detailData?.saleOrder?.totalGroupSaleOrder ||
    (detailData?.saleOrder?.groupSaleOrder || []).reduce(
      (acc: any, item: any) => ({
        totalq10: (acc.totalq10 || 0) + (Number(item.totalq10) || 0),
        totalHaolai: (acc.totalHaolai || 0) + (Number(item.totalHaolai) || 0),
        totalUnitPrice:
          (acc.totalUnitPrice || 0) + (Number(item.totalUnitPrice) || 0),
        totalprocessPrice:
          (acc.totalprocessPrice || 0) + (Number(item.totalprocessPrice) || 0),
        totalFinalPrice:
          (acc.totalFinalPrice || 0) + (Number(item.totalFinalPrice) || 0),
      }),
      {},
    );

  const t3 =
    detailData?.purchaseOrder?.totalGroupPurchaseOrder ||
    (detailData?.purchaseOrder?.groupPurchaseOrder || []).reduce(
      (acc: any, item: any) => ({
        totalq10Accounting:
          (acc.totalq10Accounting || 0) +
          (Number(item.totalq10Accounting ?? item.totalq10) || 0),
        totalFinalPrice:
          (acc.totalFinalPrice || 0) + (Number(item.totalFinalPrice) || 0),
      }),
      {},
    );

  const totalTable2UnitPrice =
    Number(t2?.totalUnitPrice ?? detailData?.totalUnitPrice) || 0;
  const totalTable2FinalPrice =
    Number(t2?.totalFinalPrice ?? detailData?.totalFinalPrice) || 0;
  const totalTable2ProcessPrice =
    Number(t2?.totalprocessPrice ?? detailData?.totalprocessPrice) || 0;
  const totalTable2q10 = Number(t2?.totalq10 ?? detailData?.totalq10) || 0;
  const totalTable2Haolai =
    Number(t2?.totalHaolai ?? detailData?.totalHaolai) || 0;

  const totalTable3FinalPrice =
    Number(t3?.totalFinalPrice ?? detailData?.totalFinalPricePurchase) || 0;
  const totalTable3q10 =
    Number(t3?.totalq10Accounting ?? detailData?.totalq10Accounting) || 0;

  const combinedQ10 = totalTable2q10;
  const combinedHaolai = totalTable2Haolai;
  const combinedQ10Tong = combinedQ10 + combinedHaolai;
  const combinedUnitPrice = totalTable2UnitPrice;
  const combinedProcessPrice = totalTable2ProcessPrice;

  const totalTable2GoldPrice = useMemo(() => {
    return (detailData?.saleOrder?.groupSaleOrder || []).reduce(
      (acc: number, item: any) => {
        const isItemGold = (Number(item?.totalUnitPrice) || 0) > 0;
        if (isItemGold) return acc;
        const goldPrice =
          item.totalGoldPrice ??
          (item.totalFinalPrice || 0) - (item.totalprocessPrice || 0);
        return acc + (Number(goldPrice) || 0);
      },
      0,
    );
  }, [detailData]);

  const combinedGoldPrice = totalTable2GoldPrice - totalTable3FinalPrice;
  const combinedTotalTT =
    totalCheckedSapDebtVal + totalTable2FinalPrice - totalTable3FinalPrice;

  const khachNoVal = combinedTotalTT > 0 ? combinedTotalTT : 0;
  const pnjpNoVal = combinedTotalTT < 0 ? -combinedTotalTT : 0;

  const chuyenKhoanVal = khachNoVal;
  const conLaiVal = chuyenKhoanVal - thucTeTransfer;

  useEffect(() => {
    if (!isUserEditedTransfer) {
      setThucTeTransfer(chuyenKhoanVal > 0 ? chuyenKhoanVal : 0);
    }
  }, [chuyenKhoanVal, isUserEditedTransfer]);

  // Sidebar checklist Select All
  const isSelectAllSapDebts = useMemo(() => {
    if (visibleSapDebts.length === 0) return false;
    return visibleSapDebts.every((x) => x.checked);
  }, [visibleSapDebts]);

  const handleSelectAllSapDebts = (checked: boolean) => {
    setIsUserEditedTransfer(false);
    setSapDebtItems((prev) =>
      prev.map((item) => {
        if (visibleSapDebts.some((v) => v.BELNR === item.BELNR)) {
          return { ...item, checked };
        }
        return item;
      }),
    );
    setIsDirty(true);
  };

  const handleToggleSapDebtItem = (targetItem: any, checked: boolean) => {
    setIsUserEditedTransfer(false);
    const targetBill = (targetItem.BILL || '').toString().trim();
    const targetBelnr = (targetItem.BELNR || '').toString().trim();
    const targetMiro = (targetItem.MIRO || '').toString().trim();
    const dmbtrVal =
      Number(targetItem.DMBTR) || Number(targetItem.paidAmount) || 0;

    // Dòng Ứng trước (dmbtrVal < 0), MIRO hoặc BILL rỗng/'-' → chỉ toggle đúng dòng đó theo BELNR
    const isAdvance = dmbtrVal < 0;
    const hasMiro = targetMiro && targetMiro !== '-';
    const isSingleItemOnly =
      isAdvance || hasMiro || !targetBill || targetBill === '-';

    setSapDebtItems((prev) =>
      prev.map((item) => {
        const itemBelnr = (item.BELNR || '').toString().trim();
        const itemDmbtr = Number(item.DMBTR) || Number(item.paidAmount) || 0;

        if (isSingleItemOnly) {
          // Không auto-group: chỉ so sánh bằng BELNR
          return itemBelnr && itemBelnr === targetBelnr
            ? { ...item, checked }
            : item;
        }

        // Bỏ qua các dòng Ứng trước (itemDmbtr < 0) khi chọn 1 dòng thường trùng BILL
        if (itemDmbtr < 0) {
          return item;
        }

        const itemBill = (item.BILL || '').toString().trim();
        const isSameBill =
          itemBill && itemBill !== '-' && targetBill === itemBill;
        const isSameBelnr =
          targetBelnr && itemBelnr && targetBelnr === itemBelnr;

        if (isSameBill || isSameBelnr) {
          return { ...item, checked };
        }
        return item;
      }),
    );
    setIsDirty(true);
  };

  // Open / Confirm Sale Order Selection
  const handleOpenSelectSalesModal = () => {
    setTempSelectedSales([...selectedSaleOrders]);
    setIsSelectSalesModalOpen(true);
  };

  const handleConfirmSelectSales = async () => {
    setIsUserEditedTransfer(false);
    setSelectedSaleOrders(tempSelectedSales);
    setIsSelectSalesModalOpen(false);
    setIsDirty(true);

    let nextCustomerId = customerIdSelected;
    let isCustomerChanged = false;

    if (tempSelectedSales.length > 0) {
      const selectedOrder = saleOrderList.find(
        (o: any) =>
          o.orderId === tempSelectedSales[0] ||
          o.order_id === tempSelectedSales[0] ||
          o.id === tempSelectedSales[0],
      );

      const orderCustId =
        selectedOrder?.customerId ||
        selectedOrder?.customer_id ||
        selectedOrder?.childCustomerId ||
        selectedOrder?.child_customer_id;

      // CHỈ KHI KHÁC NHAU MỚI REPLACE ID VÀ RELOAD LẠI CÔNG NỢ
      if (
        orderCustId &&
        String(orderCustId).trim() !== String(customerIdSelected).trim()
      ) {
        isCustomerChanged = true;
        nextCustomerId = String(orderCustId).trim();
        setCustomerIdSelected(nextCustomerId);
        form.setFieldsValue({ customerId: nextCustomerId });

        const sapCustId =
          selectedOrder?.sapCustomerId || selectedOrder?.sap_customer_id;
        const custName =
          selectedOrder?.customerName ||
          selectedOrder?.customer_name ||
          selectedOrder?.customerStoreName ||
          selectedOrder?.customer_store_name;

        setDetailData((prev: any) => ({
          ...prev,
          customerId: nextCustomerId,
          ...(sapCustId || custName
            ? {
                tcustomer: {
                  ...(prev?.tcustomer || {}),
                  id: nextCustomerId,
                  sapCustomerId: sapCustId || prev?.tcustomer?.sapCustomerId,
                  name: custName || prev?.tcustomer?.name,
                  storeName:
                    selectedOrder?.customerStoreName ||
                    selectedOrder?.customer_store_name ||
                    prev?.tcustomer?.storeName,
                },
              }
            : {}),
        }));

        if (sapCustId || custName) {
          setCustomersData((prev: any) => {
            const exists = prev?.options?.some(
              (opt: any) => opt.value === nextCustomerId,
            );
            if (!exists) {
              const label = sapCustId
                ? `${sapCustId} - ${custName}`
                : custName || nextCustomerId;
              return {
                ...prev,
                options: [
                  {
                    label,
                    value: nextCustomerId,
                    customer: {
                      id: nextCustomerId,
                      name: custName,
                      sapCustomerId: sapCustId,
                    },
                  },
                  ...(prev?.options || []),
                ],
              };
            }
            return prev;
          });
        }
      }
    }

    await fetchAvailableStatementsAndRecalculate(
      nextCustomerId,
      teamIdSelected,
      tempSelectedSales,
      selectedPurchaseOrders,
      form.getFieldValue('marketPrice') || 0,
      isCustomerChanged,
    );
  };

  // Open / Confirm Purchase Order Selection
  const handleOpenSelectPurchasesModal = () => {
    setTempSelectedPurchases([...selectedPurchaseOrders]);
    setIsSelectPurchasesModalOpen(true);
  };

  const handleConfirmSelectPurchases = async () => {
    setIsUserEditedTransfer(false);
    setSelectedPurchaseOrders(tempSelectedPurchases);
    setIsSelectPurchasesModalOpen(false);
    setIsDirty(true);
    await fetchAvailableStatementsAndRecalculate(
      customerIdSelected,
      teamIdSelected,
      selectedSaleOrders,
      tempSelectedPurchases,
      form.getFieldValue('marketPrice') || 0,
    );
  };

  // Save Payment Summary V2
  const handleSavePaymentV2 = async () => {
    if (!customerIdSelected) {
      message.warning('Vui lòng chọn khách hàng!');
      return;
    }
    setIsSaveLoading(true);
    try {
      const formValues = form.getFieldsValue();
      const isCreateNew = !sumPaymentId;
      const body = {
        customerId: customerIdSelected,
        marketPrice:
          Number(detailData?.marketPrice) ||
          Number(formValues.marketPrice) ||
          0,
        pnjpDebitAmount: pnjpNoVal,
        pnjpDebitQ10: 0,
        customerDebitAmount: isCreateNew
          ? totalSapDebtVal
          : (detailData?.customerDebitAmount ?? khachNoVal),
        customerDebitQ10: 0,
        totalQ10: totalTable2q10,
        totalHaolai: totalTable2Haolai,
        totalUnitPrice: totalTable2UnitPrice,
        totalProcessPrice: totalTable2ProcessPrice,
        totalGoldPrice: totalTable2FinalPrice,
        finalPrice: totalTable2FinalPrice - totalTable3FinalPrice,
        totalNote: formValues.totalNote || '',
        codPrice: thucTeTransfer,
        transferPrice: chuyenKhoanVal,
        purchaseDeductOrder: Number(formValues.purchaseDeductOrder) || 0,
        processPriceDeduct: 0,
        q10: combinedQ10,
        voucher: Number(formValues.voucher) || 0,
        note: formValues.note || '',
        type: 'BP100_01',
        plantId: user?.plantCode || '5001',
        teamSaleId: teamIdSelected || user?.teamSales?.[0]?.id || '',
        saleOrder: selectedSaleOrders,
        purchaseOrder: selectedPurchaseOrders,
        sapDebt: sapDebtItems
          .filter((item) => item.checked)
          .map((item) => ({
            ...item,
            DMBTR: Number(item.DMBTR) || Number(item.paidAmount) || 0,
          })),
        ...(sumPaymentId ? { sumPaymentId } : {}),
      };

      let res: any;
      if (isAddState && !sumPaymentId) {
        res = await PaymentSummaryV2Apis.createBillPayment(body);
      } else {
        res = await PaymentSummaryV2Apis.updateBillPayment(body);
      }

      if (res.code === 0 && res.data) {
        message.success('Đã lưu bảng tổng hợp thanh toán V2 thành công!');
        setIsDirty(false);
        const newId = res.data.id || sumPaymentId;
        if (newId) {
          setSumPaymentId(newId);
          navigate(`${path.PAYMENT_SUMMARY_DETAIL_V2}?id=${newId}`, {
            replace: true,
          });
          loadDetailData(newId);
        }
      } else {
        message.error(res.message || 'Lỗi khi lưu bảng kê V2');
      }
    } catch (e: any) {
      message.error(e.message || 'Lỗi khi lưu bảng kê V2');
    } finally {
      setIsSaveLoading(false);
    }
  };

  // Helper to generate QRs with optional custom split amount
  const handleGenerateQrs = async (totalAmt: number, splitAmt: number) => {
    setIsQrModalLoading(true);
    try {
      const email = user?.email || 'user@pnj.com.vn';
      const plantCode = user?.plantCode || '5001';
      let storeId = '5901';
      let terminalId = 'P05901';
      let merchantId = 'VCBQR901';

      if (plantCode === '5002') {
        storeId = '5902';
        terminalId = 'P05902';
        merchantId = 'VCBQR902';
      } else if (plantCode === '5004') {
        storeId = '5904';
        terminalId = 'P05904';
        merchantId = 'VCBQR904';
      } else if (plantCode === '5005') {
        storeId = '5905';
        terminalId = 'P05905';
        merchantId = 'VCBQR905';
      }

      const amounts: number[] = [];
      if (splitAmt <= 0 || splitAmt >= totalAmt) {
        amounts.push(totalAmt);
      } else {
        let temp = totalAmt;
        while (temp > 0) {
          if (temp >= splitAmt) {
            amounts.push(splitAmt);
            temp -= splitAmt;
          } else {
            amounts.push(temp);
            temp = 0;
          }
        }
      }

      const newQrs = await Promise.all(
        amounts.map(async (amt) => {
          const res = await PaymentSummaryV2Apis.createQrCode({
            amount: amt,
            store_id: storeId,
            terminal_id: terminalId,
            merchant_id: merchantId,
            merchant_code: merchantId,
            created_by: email,
          });
          const data = res?.data?.data || res?.data || res || {};
          const txnId =
            data.txnId ||
            data.txn_id ||
            data.transactionId ||
            data.transaction_id ||
            data.id ||
            `VCB${Date.now()}`;
          const transactionLogId =
            data.transactionLogId ||
            data.transaction_log_id ||
            data.addInfo ||
            data.add_info ||
            data.content ||
            data.note ||
            `TT_${sumPaymentId || 'PNJ'}_${amt}`;

          return {
            amount: amt,
            image:
              data.image || data.qrData || data.qr_data || data.qrCode || '',
            txnId,
            transactionLogId,
            status: data.status || 'PENDING',
          };
        }),
      );

      // Chuyển tất cả mã QR đang active cũ sang CANCELLED (Lịch sử bị hủy/thay thế)
      const oldCancelledQrs = generatedQrs.map((qr) =>
        (qr.status || '').toString().toUpperCase() !== 'CANCELLED'
          ? { ...qr, status: 'CANCELLED' }
          : qr,
      );

      const combinedQrs = [...oldCancelledQrs, ...newQrs];
      setGeneratedQrs(combinedQrs);
      setQrList(combinedQrs);

      // Tự động lưu tức thì danh sách mã QR mới vào Backend CSDL
      if (sumPaymentId) {
        await handleSaveQrCodes(combinedQrs, true);
      }
    } catch (e: any) {
      message.error(e.message || 'Lỗi khi sinh mã QR');
    } finally {
      setIsQrModalLoading(false);
    }
  };

  // QR Modal logic (matching Mobile V2 100%)
  const handleOpenQrGenerator = async () => {
    if (!sumPaymentId) {
      message.warning(
        'Vui lòng lưu bảng tổng hợp thanh toán V2 trước khi tạo QR!',
      );
      return;
    }
    if (isDirty) {
      message.warning('Vui lòng lưu bảng kê trước khi tạo mã QR!');
      return;
    }
    const transferAmt =
      thucTeTransfer ||
      Number(form.getFieldValue('transferPrice')) ||
      Number(form.getFieldValue('codPrice')) ||
      0;
    if (transferAmt <= 0) {
      message.warning(
        'Vui lòng nhập Số tiền thực tế chuyển khoản lớn hơn 0 để tạo QR',
      );
      return;
    }
    setQrAmount(transferAmt);
    setSplitAmountInput(0);
    setIsQrModalOpen(true);
    setIsQrModalLoading(true);

    try {
      const qrRes = await PaymentSummaryV2Apis.getMobileQrCodes(sumPaymentId);
      if (
        qrRes?.code === 0 &&
        Array.isArray(qrRes.data) &&
        qrRes.data.length > 0
      ) {
        const activeQrs = qrRes.data.filter(
          (q: any) => q.status !== 'CANCELLED',
        );
        setGeneratedQrs(qrRes.data);
        setQrList(qrRes.data);
        if (activeQrs.length === 0) {
          await handleGenerateQrs(transferAmt, 0);
        }
      } else {
        await handleGenerateQrs(transferAmt, 0);
      }
    } catch (e) {
      await handleGenerateQrs(transferAmt, 0);
    } finally {
      setIsQrModalLoading(false);
    }
  };

  const handleSaveQrCodes = async (customQrs?: any[], isSilent?: boolean) => {
    setIsQrModalLoading(true);
    try {
      const listToSave = customQrs || generatedQrs;
      const qrsToSave = listToSave.map((qr) => ({
        txnId: qr.txnId,
        amount: qr.amount,
        image: qr.image,
        status: qr.status,
        transactionLogId: qr.transactionLogId,
      }));

      const res = await PaymentSummaryV2Apis.saveQr({
        paymentId: sumPaymentId,
        totalNote: JSON.stringify(listToSave),
        qrs: qrsToSave,
      });

      if (res) {
        if (!isSilent) {
          message.success('Đã lưu mã QR thành công!');
          setIsQrModalOpen(false);
        }
        const qrRes = await PaymentSummaryV2Apis.getMobileQrCodes(sumPaymentId);
        if (qrRes?.code === 0) {
          const list = qrRes.data || [];
          setQrList(list);
          setGeneratedQrs(list);
        }
      }
    } catch (e: any) {
      message.error(e.message || 'Lỗi khi lưu mã QR');
    } finally {
      setIsQrModalLoading(false);
    }
  };

  const handleCancelAllQrs = async () => {
    const cancelled = generatedQrs.map((qr) => ({
      ...qr,
      status: 'CANCELLED',
    }));
    setGeneratedQrs(cancelled);
    await handleSaveQrCodes(cancelled);
  };

  const handleCheckAllQrStatus = async () => {
    if (!sumPaymentId) return;
    setIsCheckingAllQrStatus(true);
    try {
      const res = await PaymentSummaryV2Apis.checkAllQrStatus({
        paymentId: sumPaymentId,
      });
      if (res.code === 0) {
        message.success(res.message || 'Kiểm tra hoàn tất!');
        const qrRes = await PaymentSummaryV2Apis.getMobileQrCodes(sumPaymentId);
        if (qrRes.code === 0) {
          setQrList(qrRes.data || []);
        }
        await fetchPaymentDetails(sumPaymentId);
      } else {
        message.error(res.message || 'Không thể kết nối API');
      }
    } catch (e: any) {
      message.error(e.message || 'Lỗi kiểm tra trạng thái QR');
    } finally {
      setIsCheckingAllQrStatus(false);
    }
  };

  const handleExportHddt = async () => {
    if (!sumPaymentId || isDirty) {
      message.warning(
        'Vui lòng lưu bảng tổng hợp thanh toán trước khi xuất HĐĐT!',
      );
      return;
    }
    try {
      if (selectedSaleOrders.length === 0) {
        message.warning('Chưa chọn bảng kê để xuất hóa đơn!');
        return;
      }
      setExportHddtLoading(true);
      const res = await PaymentSummaryV2Apis.exportHddtV2({
        paymentId: sumPaymentId,
        orderIds: selectedSaleOrders,
      });
      if (res.code === 0 && res.data) {
        setHddtOrders(res.data);
        setIsHddtModalOpen(true);
      } else {
        message.error(res.message || 'Lỗi tải trạng thái HĐĐT');
      }
    } catch (e: any) {
      message.error(e.message || 'Lỗi xuất HĐĐT');
    } finally {
      setExportHddtLoading(false);
    }
  };

  const handleCreateBilling = async (item: any) => {
    try {
      message.loading({ content: 'Đang tạo BILLING...', key: 'hddt_action' });
      const body = {
        Key: item.orderId || '',
        Delivery: item.doId || '',
        GoodIssue: item.giId || '',
        Year: (item.year || '').toString(),
        Output: { results: [] },
        Return: { results: [] },
      };
      await PaymentSummaryV2Apis.createBillSap(body);
      message.success({
        content: 'Yêu cầu tạo BILLING thành công!',
        key: 'hddt_action',
      });
      handleExportHddt();
    } catch (e: any) {
      message.error({
        content: e.message || 'Lỗi khi tạo BILLING',
        key: 'hddt_action',
      });
    }
  };

  const handleCreateEinvoice = async (item: any) => {
    try {
      message.loading({
        content: 'Đang tạo hóa đơn điện tử...',
        key: 'hddt_action',
      });
      const body = {
        PVBELN: item.billingId || '',
        IV_NAME: 'MDMS',
        IV_UNAME: 'MDMS',
      };
      await PaymentSummaryV2Apis.createEinvoiceSap(item.orderId, body);
      message.success({
        content: 'Yêu cầu tạo HĐĐT thành công!',
        key: 'hddt_action',
      });
      handleExportHddt();
    } catch (e: any) {
      message.error({
        content: e.message || 'Lỗi khi tạo HĐĐT',
        key: 'hddt_action',
      });
    }
  };

  const handleResendEinvoice = async (
    item: any,
    channel: 'EMAIL' | 'SMS' | 'ALL',
  ) => {
    if (!user?.isAccountant) {
      try {
        const soCode = item.soId || item.orderId;
        if (soCode) {
          const quotaRes =
            await EinvoiceHistoryApis.getEinvoiceResendQuota(soCode);
          const quota = quotaRes?.data || quotaRes;
          if (channel === 'EMAIL' && !quota?.email?.canResend) {
            message.warning(
              quota?.email?.successCount > 0
                ? 'Hóa đơn đã được gửi thành công qua Email. Không thể gửi lại.'
                : 'Kênh Email đã vượt quá số lần gửi lại tối đa (3 lần).',
            );
            return;
          }
          if (channel === 'SMS' && !quota?.sms?.canResend) {
            message.warning(
              quota?.sms?.successCount > 0
                ? 'Hóa đơn đã được gửi thành công qua SMS. Không thể gửi lại.'
                : 'Kênh SMS đã vượt quá số lần gửi lại tối đa (3 lần).',
            );
            return;
          }
          if (channel === 'ALL' && !quota?.canResendAll) {
            message.warning(
              'Không đủ điều kiện gửi lại tất cả các kênh (kênh đã thành công hoặc đã gửi tối đa 3 lần).',
            );
            return;
          }
        }
      } catch (err) {
        // Fallback: để API backend /sap/resend-einvoice kiểm tra và chặn
      }
    }

    const channels = channel === 'ALL' ? ['EMAIL', 'SMS'] : [channel];
    const channelLabel =
      channel === 'ALL' ? 'Email & SMS' : channel === 'EMAIL' ? 'Email' : 'SMS';
    try {
      message.loading({
        content: `Đang gửi lại HĐĐT qua ${channelLabel}...`,
        key: 'hddt_resend',
      });
      const custObj = detailData?.tcustomer;
      const custType =
        custObj?.typeSap === '1'
          ? 'Doanh nghiệp'
          : custObj?.typeSap === '2'
            ? 'Cá nhân'
            : custObj?.typeSap || (custObj?.taxId ? 'Doanh nghiệp' : 'Cá nhân');
      let custFullName = '';
      if (custObj) {
        if (custObj.typeSap === '1') {
          const parts = [
            custObj.shortName,
            custObj.storeName,
            custObj.name,
          ].filter(Boolean);
          const uniqueParts = Array.from(new Set(parts));
          custFullName =
            uniqueParts.join(' - ') || custObj.storeName || custObj.name || '';
        } else if (custObj.firstName && custObj.name) {
          custFullName =
            custObj.firstName === custObj.name
              ? custObj.name
              : `${custObj.firstName} ${custObj.name}`;
        } else {
          custFullName = custObj.name || custObj.storeName || '';
        }
      }

      const res: any = await EinvoiceHistoryApis.resendEinvoice({
        soCode: item.soId || item.orderId,
        channels: channels,
        invoiceCode: item.envoiceId,
        billNum: item.billingId,
        lookupUrl: item.link || item.einvoiceUrl || '',
        customerId: custObj?.sapCustomerId || custObj?.id || customerIdSelected,
        customerName: custFullName,
        customerType: custType,
        taxCode: custObj?.taxId || custObj?.cccd || '',
        customerEmail: custObj?.email || '',
        customerPhone: custObj?.phone || '',
      });
      const isSuccess = res?.success || res?.data?.success;
      if (isSuccess) {
        message.success({
          content:
            res?.message ||
            res?.data?.message ||
            `Yêu cầu gửi lại qua ${channelLabel} đã được tiếp nhận!`,
          key: 'hddt_resend',
        });
      } else {
        message.error({
          content:
            res?.message ||
            res?.data?.message ||
            `Gửi lại qua ${channelLabel} thất bại`,
          key: 'hddt_resend',
        });
      }
    } catch (e: any) {
      message.error({
        content:
          e?.response?.data?.message ||
          e?.message ||
          `Lỗi khi gửi lại qua ${channelLabel}`,
        key: 'hddt_resend',
      });
    }
  };

  const fetchPaymentDetails = async (idToUse?: string) => {
    const targetId = idToUse || sumPaymentId;
    if (!targetId) return;
    try {
      setPaymentDetailsLoading(true);
      const res = await PaymentSummaryV2Apis.getMobilePaymentDetails(targetId);
      if (res.code === 0 && res.data) {
        setPaymentDetailsLines(res.data.listLines || []);
        setIsFullyPaid(!!res.data.isFullyPaid);
      }
    } catch (e: any) {
      message.error(e.message || 'Lỗi khi tải chi tiết thanh toán');
    } finally {
      setPaymentDetailsLoading(false);
    }
  };

  const handleOpenPaymentDetails = () => {
    if (!sumPaymentId) {
      message.warning(
        'Vui lòng lưu bảng kê trước khi xem Chi tiết thanh toán!',
      );
      return;
    }
    setIsPaymentDetailsModalOpen(true);
    fetchPaymentDetails();
  };

  const handleSendSapFull = async () => {
    if (!sumPaymentId) return;
    try {
      setSendingSapFull(true);
      const res = await PaymentSummaryV2Apis.sendSapFull({
        paymentId: sumPaymentId,
      });
      if (res.code === 0) {
        message.success('Gửi SAP thành công!');
        fetchPaymentDetails();
      } else {
        message.error(res.message || 'Không thể gửi SAP');
      }
    } catch (e: any) {
      message.error(e.message || 'Lỗi khi gửi SAP');
    } finally {
      setSendingSapFull(false);
    }
  };

  const handleSendSapPartial = async (line: any) => {
    if (!sumPaymentId) return;
    try {
      message.loading({ content: 'Đang gửi lại SAP...', key: 'send_sap_row' });
      const res = await PaymentSummaryV2Apis.sendSapPartial({
        paymentId: sumPaymentId,
        detailId: line.id,
        orderId: line.orderId,
      });
      if (res.code === 0) {
        message.success({
          content: 'Gửi SAP thành công!',
          key: 'send_sap_row',
        });
        fetchPaymentDetails();
      } else {
        message.error({
          content: res.message || 'Không thể gửi SAP',
          key: 'send_sap_row',
        });
      }
    } catch (e: any) {
      message.error({
        content: e.message || 'Lỗi khi gửi SAP',
        key: 'send_sap_row',
      });
    }
  };

  const handleDeletePaymentSummary = () => {
    if (!sumPaymentId) return;
    Modal.confirm({
      title: 'Xác nhận xóa',
      icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
      content:
        'Bạn có chắc chắn muốn xóa bảng Tổng hợp thanh toán này không? Hành động này không thể hoàn tác.',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const res = await PaymentSummaryV2Apis.deleteBillPaymentV2({
            id: sumPaymentId,
          });
          if (res) {
            message.success('Xóa bảng THTT thành công');
            navigate(path.PAYMENT_SUMMARY_V2);
          }
        } catch (e: any) {
          message.error(e.message || 'Lỗi khi xóa bảng THTT');
        }
      },
    });
  };

  // Dropdown menu for Print & Export options
  const exportMenuItems: MenuProps['items'] = [
    {
      key: 'excel',
      label: 'Xuất bảng tổng hợp Excel',
      onClick: () => message.info('Đã tải xuống file Excel'),
    },
    {
      key: 'pdf',
      label: 'Xuất bảng tổng hợp PDF',
      onClick: () => message.info('Đã tải xuống file PDF'),
    },
    {
      key: 'print',
      label: 'In bảng tổng hợp',
      onClick: () => window.print(),
    },
  ];

  const allThttSoCodes = useMemo(() => {
    const list: string[] = [];
    (detailData?.saleOrder?.groupSaleOrder || []).forEach((g: any) => {
      if (g.soId) list.push(g.soId);
      if (g.orderId) list.push(g.orderId);
    });
    (detailData?.listSaleOrder || []).forEach((s: any) => {
      if (s.soId) list.push(s.soId);
      if (s.orderId) list.push(s.orderId);
      if (s.sapOrderId) list.push(s.sapOrderId);
    });
    return Array.from(new Set(list)).filter(Boolean);
  }, [detailData]);

  const moreMenuItems: MenuProps['items'] = [
    {
      key: 'einvoice_history',
      label: 'Lịch sử gửi HĐĐT',
      icon: <HistoryOutlined style={{ color: '#0052cc' }} />,
      disabled: !sumPaymentId,
      onClick: () => setIsEinvoiceHistoryModalOpen(true),
    },
    {
      key: 'delete',
      label: 'Xóa bảng THTT',
      danger: true,
      disabled: !sumPaymentId || qrList.length > 0,
      onClick: handleDeletePaymentSummary,
    },
  ];

  // Action Button Styles matching Mobile V2 exact design & pill shape
  const pillButtonStyle = (
    bgColor: string,
    textColor: string,
    borderColor?: string,
    disabled = false,
  ): React.CSSProperties => ({
    borderRadius: 20,
    height: 38,
    padding: '0 18px',
    fontWeight: 600,
    fontSize: 14,
    backgroundColor: disabled ? '#f5f5f5' : bgColor,
    color: disabled ? '#bfbfbf' : textColor,
    border: borderColor
      ? `1px solid ${disabled ? '#d9d9d9' : borderColor}`
      : 'none',
    boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
  });

  return (
    <Spin spinning={isDetailLoading} tip="Đang tải dữ liệu chi tiết Bảng kê...">
      <div>
        {/* Top Action Header Buttons Bar (Clean Pill Buttons matching Mobile) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
            padding: '12px 16px',
            backgroundColor: '#fff',
            borderRadius: 12,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}
        >
          <div>
            {!allSelectedOrdersHaveBilling && (
              <Space style={{ color: '#d9363e', fontWeight: 600 }}>
                <WarningOutlined />
                <span>Bảng kê chưa có mã Billing. Vui lòng kiểm tra lại!</span>
              </Space>
            )}
          </div>

          <Space size="small" wrap>
            {/* 1. Chi tiết thanh toán */}
            <Button
              style={pillButtonStyle('#e4f3ff', '#0052cc', '#b3d8ff')}
              onClick={handleOpenPaymentDetails}
            >
              Chi tiết thanh toán
            </Button>

            {/* 2. Tạo / Xem QR */}
            <Button
              style={pillButtonStyle(
                '#00b4d8',
                '#ffffff',
                undefined,
                !sumPaymentId || isDirty || !allSelectedOrdersHaveBilling,
              )}
              disabled={
                !sumPaymentId || isDirty || !allSelectedOrdersHaveBilling
              }
              onClick={handleOpenQrGenerator}
            >
              Tạo / Xem QR
            </Button>

            {/* 3. Kiểm tra kết quả */}
            {sumPaymentId && (
              <Button
                style={pillButtonStyle(
                  '#10b981',
                  '#ffffff',
                  undefined,
                  !hasActiveQr ||
                    aggregatePaymentStatus === 'Đã Thanh Toán' ||
                    isCheckingAllQrStatus,
                )}
                disabled={
                  !hasActiveQr ||
                  aggregatePaymentStatus === 'Đã Thanh Toán' ||
                  isCheckingAllQrStatus
                }
                loading={isCheckingAllQrStatus}
                onClick={handleCheckAllQrStatus}
              >
                Kiểm tra kết quả
              </Button>
            )}

            {/* 4. Lưu */}
            <Button
              style={pillButtonStyle(
                '#208b3a',
                '#ffffff',
                undefined,
                hasActiveQr || isSaveLoading,
              )}
              disabled={hasActiveQr || isSaveLoading}
              loading={isSaveLoading}
              onClick={handleSavePaymentV2}
            >
              Lưu
            </Button>

            {/* 5. Xuất HĐĐT */}
            <Button
              style={pillButtonStyle(
                '#f1b44c',
                '#ffffff',
                undefined,
                !sumPaymentId || isDirty,
              )}
              disabled={!sumPaymentId || isDirty}
              onClick={handleExportHddt}
            >
              Xuất HĐĐT
            </Button>

            {/* 6. Xuất in (Dropdown) */}
            <Dropdown
              menu={{ items: exportMenuItems }}
              disabled={!sumPaymentId || isDirty}
            >
              <Button
                style={pillButtonStyle(
                  '#ffffff',
                  '#0052cc',
                  '#0052cc',
                  !sumPaymentId || isDirty,
                )}
                icon={<PrinterOutlined />}
              >
                Xuất in <DownOutlined />
              </Button>
            </Dropdown>

            {/* 7. More Options Button (...) */}
            <Dropdown menu={{ items: moreMenuItems }} trigger={['click']}>
              <Button
                shape="circle"
                style={{
                  width: 38,
                  height: 38,
                  borderColor: '#0052cc',
                  color: '#0052cc',
                }}
                icon={<MoreOutlined style={{ fontSize: 18 }} />}
              />
            </Dropdown>
          </Space>
        </div>

        <Form form={form} layout="vertical">
          {/* Main 2-column layout matching mobile V2 */}
          <Row gutter={[16, 16]}>
            {/* LEFT SIDEBAR (Width 320px / Span 6) */}
            <Col xs={24} lg={7} xl={6}>
              {/* Card 1: Thông tin chung */}
              <Card
                size="small"
                title="Bảng tổng hợp thanh toán"
                style={{ borderRadius: 12, marginBottom: 16 }}
              >
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Mã THTT
                  </Text>
                  <div>
                    <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                      {sumPaymentId || '_'}
                    </Text>
                  </div>
                </div>

                {/* Giá bóng: Key-Value text display matching Mobile V2 */}
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Giá bóng
                  </Text>
                  <div>
                    <Text strong style={{ fontSize: 16 }}>
                      {formatNumber(
                        detailData?.marketPrice ||
                          form.getFieldValue('marketPrice') ||
                          0,
                      )}{' '}
                      VND
                    </Text>
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Khách hàng
                  </Text>
                  {isAddState ? (
                    <Form.Item
                      name="customerId"
                      noStyle
                      rules={[{ required: true, message: 'Chọn khách hàng' }]}
                    >
                      <LazySelectForm
                        placeholder="Chọn khách hàng"
                        allowClear
                        data={customerData}
                        loading={isGetCustomersPending}
                        onSelectScrollChange={handleCustomerFetch}
                        onChange={handleCustomerChange}
                      />
                    </Form.Item>
                  ) : (
                    <div>
                      <Text strong style={{ fontSize: 15 }}>
                        {detailData?.tcustomer?.sapCustomerId} -{' '}
                        {detailData?.tcustomer?.storeName ||
                          detailData?.tcustomer?.name}
                      </Text>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    marginBottom: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text type="secondary">Tổng nợ của khách hàng:</Text>
                  <Text strong>
                    {formatNumber(
                      detailData?.id && detailData?.customerDebitAmount != null
                        ? Number(detailData.customerDebitAmount)
                        : totalSapDebtVal,
                    )}
                  </Text>
                </div>

                <div
                  style={{
                    marginBottom: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text type="secondary">Tổng nợ cần trả:</Text>
                  <Text strong style={{ color: '#1890ff' }}>
                    {formatNumber(totalCheckedSapDebtVal)}
                  </Text>
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text type="secondary">Trạng thái thanh toán:</Text>
                  <Tag color={aggregatePaymentStatusColor}>
                    {aggregatePaymentStatus}
                  </Tag>
                </div>
              </Card>

              {/* Card 2: Checklist Công nợ SAP */}
              {visibleSapDebts.length > 0 && (
                <Card
                  size="small"
                  title={
                    <Checkbox
                      checked={isSelectAllSapDebts}
                      disabled={hasActiveQr}
                      onChange={(e) =>
                        handleSelectAllSapDebts(e.target.checked)
                      }
                    >
                      <Text strong>Chọn hết công nợ SAP</Text>
                    </Checkbox>
                  }
                  style={{ borderRadius: 12, marginBottom: 16 }}
                >
                  <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                    {visibleSapDebts.map((item) => {
                      const bill = (item.BILL || '').toString().trim();
                      const miro = (item.MIRO || '').toString().trim();
                      const belnr = (item.BELNR || '').toString().trim();

                      const dmbtrVal =
                        Number(item.DMBTR) || Number(item.paidAmount) || 0;

                      let displayCode =
                        miro && miro !== '-'
                          ? miro
                          : bill && bill !== '-'
                            ? bill
                            : belnr;
                      if (dmbtrVal < 0 && belnr && belnr !== '-') {
                        displayCode = belnr;
                      }

                      return (
                        <div
                          key={item.BELNR}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '6px 0',
                            borderBottom: '1px solid #f0f0f0',
                          }}
                        >
                          <Checkbox
                            checked={!!item.checked}
                            disabled={hasActiveQr}
                            onChange={(e) =>
                              handleToggleSapDebtItem(item, e.target.checked)
                            }
                          >
                            <Text style={{ fontSize: 13 }}>
                              {displayCode} ({formatNumber(dmbtrVal)})
                            </Text>
                          </Checkbox>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}

              {/* Card 3: Audit Info */}
              <Card size="small" style={{ borderRadius: 12 }}>
                <div style={{ fontSize: 12, lineHeight: '20px' }}>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Text type="secondary">Ngày tạo:</Text>
                    <Text>
                      {detailData?.createDate
                        ? dayjs(detailData.createDate).format(
                            'DD/MM/YYYY HH:mm:ss',
                          )
                        : '-'}
                    </Text>
                  </div>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Text type="secondary">Người tạo:</Text>
                    <Text>
                      {detailData?.createName ||
                        detailData?.tcreateUser?.fullName ||
                        detailData?.createId ||
                        user?.fullName ||
                        user?.email ||
                        '-'}
                    </Text>
                  </div>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Text type="secondary">Cập nhật:</Text>
                    <Text>
                      {detailData?.modifyDate
                        ? dayjs(detailData.modifyDate).format(
                            'DD/MM/YYYY HH:mm:ss',
                          )
                        : '-'}
                    </Text>
                  </div>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Text type="secondary">Người cập nhật:</Text>
                    <Text>
                      {detailData?.modifyName ||
                        detailData?.tmodifyUser?.fullName ||
                        detailData?.modifyId ||
                        user?.fullName ||
                        user?.email ||
                        '-'}
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>

            {/* RIGHT MAIN CONTENT AREA (Span 17/18) */}
            <Col xs={24} lg={17} xl={18}>
              {/* Header stats & Button matching screenshot */}
              {sumPaymentId && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  {einvoiceSummary.total > 0 && (
                    <Text style={{ fontSize: 13, color: '#595959' }}>
                      {`${einvoiceSummary.success}/${einvoiceSummary.total} hóa đơn gửi khách hàng thành công`}
                    </Text>
                  )}
                  <Button
                    disabled={einvoiceSummary.total === 0}
                    style={{
                      borderRadius: 16,
                      height: 32,
                      padding: '0 16px',
                      fontWeight: 600,
                      fontSize: 13,
                      backgroundColor:
                        einvoiceSummary.total > 0 ? '#00ba51' : '#d9d9d9',
                      color: '#ffffff',
                      border: 'none',
                      boxShadow:
                        einvoiceSummary.total > 0
                          ? '0 2px 4px rgba(0, 186, 81, 0.2)'
                          : 'none',
                      cursor:
                        einvoiceSummary.total > 0 ? 'pointer' : 'not-allowed',
                    }}
                    onClick={() => {
                      if (einvoiceSummary.total > 0) {
                        setIsEinvoiceHistoryModalOpen(true);
                      }
                    }}
                  >
                    Lịch sử gửi HĐ
                  </Button>
                </div>
              )}

              {/* Panel 1: 1. Công nợ */}
              <Card
                size="small"
                title={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsCongNoExpanded(!isCongNoExpanded)}
                  >
                    <Space>
                      {isCongNoExpanded ? (
                        <CaretUpOutlined />
                      ) : (
                        <CaretDownOutlined />
                      )}
                      <Text strong style={{ fontSize: 16 }}>
                        1. Công nợ
                      </Text>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Tiền tệ: VND
                    </Text>
                  </div>
                }
                style={{ borderRadius: 12, marginBottom: 16 }}
              >
                {isCongNoExpanded && (
                  <Table
                    dataSource={checkedSapDebts}
                    pagination={false}
                    rowKey="BELNR"
                    size="small"
                    bordered
                    columns={[
                      {
                        title: 'Mã chứng từ',
                        dataIndex: 'BELNR',
                        key: 'BELNR',
                      },
                      {
                        title: 'Billing',
                        dataIndex: 'BILL',
                        key: 'BILL',
                        render: (val, record) =>
                          record.MIRO && record.MIRO !== '-' ? '-' : val || '-',
                      },
                      {
                        title: 'Miro',
                        dataIndex: 'MIRO',
                        key: 'MIRO',
                        render: (val) => val || '-',
                      },
                      {
                        title: 'Bảng kê / Dẻ',
                        dataIndex: 'VBELN',
                        key: 'VBELN',
                        render: (val) => val || '-',
                      },
                      {
                        title: 'THTT',
                        dataIndex: 'THTT',
                        key: 'THTT',
                        render: (val) => val || '-',
                      },
                      {
                        title: 'Tổng TT',
                        dataIndex: 'DMBTR',
                        key: 'DMBTR',
                        align: 'right',
                        render: (val, record) =>
                          formatNumber(
                            Number(val) || Number(record.paidAmount) || 0,
                          ),
                      },
                    ]}
                    summary={() => (
                      <Table.Summary.Row
                        style={{
                          backgroundColor: '#fafafa',
                          fontWeight: 'bold',
                        }}
                      >
                        <Table.Summary.Cell
                          index={0}
                          colSpan={5}
                          align="center"
                        >
                          Tổng
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          {formatNumber(totalCheckedSapDebtVal)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                )}
              </Card>

              {/* Panel 2: 2. Bảng kê */}
              <Card
                size="small"
                title={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsBangKeExpanded(!isBangKeExpanded)}
                  >
                    <Space>
                      {isBangKeExpanded ? (
                        <CaretUpOutlined />
                      ) : (
                        <CaretDownOutlined />
                      )}
                      <Text strong style={{ fontSize: 16 }}>
                        2. Bảng kê
                      </Text>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Tiền tệ: VND
                    </Text>
                  </div>
                }
                style={{ borderRadius: 12, marginBottom: 16 }}
              >
                {isBangKeExpanded && (
                  <div>
                    {/* Select Order Fake Combobox */}
                    <div
                      onClick={
                        hasActiveQr ? undefined : handleOpenSelectSalesModal
                      }
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: 6,
                        cursor: hasActiveQr ? 'not-allowed' : 'pointer',
                        backgroundColor: hasActiveQr ? '#f5f5f5' : '#fafafa',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 12,
                      }}
                    >
                      <Text
                        color={
                          selectedSaleOrders.length > 0 ? '#000' : '#bfbfbf'
                        }
                      >
                        {selectedSaleOrders.length > 0
                          ? selectedSaleOrders.join('; ')
                          : 'Chọn bảng kê bán hàng'}
                      </Text>
                      <DownOutlined style={{ color: '#bfbfbf' }} />
                    </div>

                    {/* Matched SAP Debt Debug Card */}
                    {selectedSaleOrders.length > 0 &&
                      matchedDebts.length > 0 && (
                        <div
                          style={{
                            padding: 10,
                            backgroundColor: '#fffbe6',
                            border: '1px solid #ffe58f',
                            borderRadius: 6,
                            marginBottom: 12,
                            fontSize: 12,
                          }}
                        >
                          <Text strong style={{ color: '#fa8c16' }}>
                            Công nợ SAP tương ứng với Bảng kê:
                          </Text>
                          {matchedDebts.map((item) => (
                            <div key={item.BELNR} style={{ marginTop: 2 }}>
                              • Doc: {item.BELNR} | Bill: {item.BILL || '-'} |
                              DMBTR: {formatNumber(Number(item.DMBTR) || 0)}{' '}
                              {item.checked ? '(Đã chọn)' : ''}
                            </div>
                          ))}
                        </div>
                      )}

                    {/* Group Sale Order Table */}
                    <Table
                      dataSource={detailData?.saleOrder?.groupSaleOrder || []}
                      pagination={false}
                      rowKey="age"
                      size="small"
                      bordered
                      columns={[
                        {
                          title: 'Tuổi vàng',
                          dataIndex: 'age',
                          key: 'age',
                          render: (val) => val || '-',
                        },
                        {
                          title: 'TL (chi)',
                          dataIndex: 'totalGoldWeightThread',
                          key: 'totalGoldWeightThread',
                          align: 'right',
                          render: (val) =>
                            val != null
                              ? formatCurrency({
                                  value: Number(val),
                                  format: 4,
                                })
                              : '-',
                        },
                        {
                          title: 'TL Q10(chỉ)',
                          dataIndex: 'totalq10',
                          key: 'totalq10',
                          align: 'right',
                          render: (val) =>
                            val != null
                              ? formatCurrency({
                                  value: Number(val),
                                  format: 4,
                                })
                              : '-',
                        },
                        {
                          title: 'Hao lai',
                          dataIndex: 'totalHaolai',
                          key: 'totalHaolai',
                          align: 'right',
                          render: (val) =>
                            val != null
                              ? formatCurrency({
                                  value: Number(val),
                                  format: 4,
                                })
                              : '-',
                        },
                        {
                          title: 'Q10 tổng',
                          dataIndex: 'totalQ10Tong',
                          key: 'totalQ10Tong',
                          align: 'right',
                          render: (val, record: any) => {
                            const q10Tong =
                              val ??
                              record?.totalq10T ??
                              (Number(record?.totalq10) || 0) +
                                (Number(record?.totalHaolai) || 0);
                            return q10Tong != null
                              ? formatCurrency({
                                  value: Number(q10Tong),
                                  format: 4,
                                })
                              : '-';
                          },
                        },
                        {
                          title: 'TT món',
                          dataIndex: 'totalUnitPrice',
                          key: 'totalUnitPrice',
                          align: 'right',
                          render: (val) => formatNumber(val || 0),
                        },
                        {
                          title: 'TT vàng',
                          dataIndex: 'totalGoldPrice',
                          key: 'totalGoldPrice',
                          align: 'right',
                          render: (val, record: any) => {
                            const goldPrice =
                              val ??
                              Math.max(
                                0,
                                (record.totalFinalPrice || 0) -
                                  (record.totalUnitPrice || 0) -
                                  (record.totalprocessPrice || 0),
                              );
                            return formatNumber(goldPrice || 0);
                          },
                        },
                        {
                          title: 'TT công',
                          dataIndex: 'totalprocessPrice',
                          key: 'totalprocessPrice',
                          align: 'right',
                          render: (val) => formatNumber(val || 0),
                        },
                        {
                          title: 'Thành tiền',
                          dataIndex: 'totalFinalPrice',
                          key: 'totalFinalPrice',
                          align: 'right',
                          render: (val) => formatNumber(val),
                        },
                      ]}
                      summary={() => {
                        const groupList =
                          detailData?.saleOrder?.groupSaleOrder || [];
                        if (groupList.length === 0) return null;
                        const totalRow =
                          detailData?.saleOrder?.totalGroupSaleOrder ||
                          groupList.reduce(
                            (acc: any, item: any) => ({
                              totalGoldWeightThread:
                                (acc.totalGoldWeightThread || 0) +
                                (Number(item.totalGoldWeightThread) || 0),
                              totalq10:
                                (acc.totalq10 || 0) +
                                (Number(item.totalq10) || 0),
                              totalHaolai:
                                (acc.totalHaolai || 0) +
                                (Number(item.totalHaolai) || 0),
                              totalQ10Tong:
                                (acc.totalQ10Tong || 0) +
                                (Number(
                                  item.totalQ10Tong ??
                                    item.totalq10T ??
                                    (Number(item.totalq10) || 0) +
                                      (Number(item.totalHaolai) || 0),
                                ) || 0),
                              totalUnitPrice:
                                (acc.totalUnitPrice || 0) +
                                (Number(item.totalUnitPrice) || 0),
                              totalprocessPrice:
                                (acc.totalprocessPrice || 0) +
                                (Number(item.totalprocessPrice) || 0),
                              totalFinalPrice:
                                (acc.totalFinalPrice || 0) +
                                (Number(item.totalFinalPrice) || 0),
                            }),
                            {},
                          );
                        const totalGoldPrice = (
                          detailData?.saleOrder?.groupSaleOrder || []
                        ).reduce((acc: number, item: any) => {
                          const isItemGold =
                            (Number(item?.totalUnitPrice) || 0) > 0;
                          if (isItemGold) return acc;
                          const goldPrice =
                            item.totalGoldPrice ??
                            (item.totalFinalPrice || 0) -
                              (item.totalprocessPrice || 0);
                          return acc + (Number(goldPrice) || 0);
                        }, 0);
                        return (
                          <Table.Summary.Row
                            style={{
                              backgroundColor: '#fafafa',
                              fontWeight: 'bold',
                            }}
                          >
                            <Table.Summary.Cell index={0} align="center">
                              Tổng
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">
                              {formatCurrency({
                                value: Number(
                                  totalRow.totalGoldWeightThread || 0,
                                ),
                                format: 4,
                              })}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} align="right">
                              {formatCurrency({
                                value: Number(totalRow.totalq10 || 0),
                                format: 4,
                              })}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} align="right">
                              {formatCurrency({
                                value: Number(totalRow.totalHaolai || 0),
                                format: 4,
                              })}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={4} align="right">
                              {formatCurrency({
                                value: Number(
                                  (Number(totalRow.totalq10) || 0) +
                                    (Number(totalRow.totalHaolai) || 0),
                                ),
                                format: 4,
                              })}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={5} align="right">
                              {formatNumber(totalRow.totalUnitPrice || 0)}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={6} align="right">
                              {formatNumber(totalGoldPrice || 0)}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={7} align="right">
                              {formatNumber(totalRow.totalprocessPrice || 0)}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={8} align="right">
                              {formatNumber(totalRow.totalFinalPrice)}
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        );
                      }}
                    />
                  </div>
                )}
              </Card>

              {/* Panel 3: 3. Bảng dẻ */}
              <Card
                size="small"
                title={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsBangDeExpanded(!isBangDeExpanded)}
                  >
                    <Space>
                      {isBangDeExpanded ? (
                        <CaretUpOutlined />
                      ) : (
                        <CaretDownOutlined />
                      )}
                      <Text strong style={{ fontSize: 16 }}>
                        3. Bảng dẻ
                      </Text>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Tiền tệ: VND
                    </Text>
                  </div>
                }
                style={{ borderRadius: 12, marginBottom: 16 }}
              >
                {isBangDeExpanded && (
                  <div>
                    {/* Select Purchase Order Fake Combobox */}
                    <div
                      onClick={
                        hasActiveQr ? undefined : handleOpenSelectPurchasesModal
                      }
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: 6,
                        cursor: hasActiveQr ? 'not-allowed' : 'pointer',
                        backgroundColor: hasActiveQr ? '#f5f5f5' : '#fafafa',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 12,
                      }}
                    >
                      <Text
                        color={
                          selectedPurchaseOrders.length > 0 ? '#000' : '#bfbfbf'
                        }
                      >
                        {selectedPurchaseOrders.length > 0
                          ? selectedPurchaseOrders.join('; ')
                          : 'Chọn bảng thu dẻ'}
                      </Text>
                      <DownOutlined style={{ color: '#bfbfbf' }} />
                    </div>

                    {/* Group Purchase Order Table */}
                    <Table
                      dataSource={
                        detailData?.purchaseOrder?.groupPurchaseOrder || []
                      }
                      pagination={false}
                      rowKey="age"
                      size="small"
                      bordered
                      columns={[
                        {
                          title: 'Tuổi vàng',
                          dataIndex: 'age',
                          key: 'age',
                          render: (val) => formatNumber(val),
                        },
                        {
                          title: 'TL Tuổi vàng(chỉ)',
                          dataIndex: 'totalGoldWeightThread',
                          key: 'totalGoldWeightThread',
                          align: 'right',
                          render: (val) =>
                            formatCurrency({ value: Number(val), format: 4 }),
                        },
                        {
                          title: 'TL Q10(chỉ)',
                          dataIndex: 'totalq10',
                          key: 'totalq10',
                          align: 'right',
                          render: (val) =>
                            formatCurrency({ value: Number(val), format: 4 }),
                        },
                        {
                          title: 'TL Q10 sau ĐC(chỉ)',
                          dataIndex: 'totalq10Accounting',
                          key: 'totalq10Accounting',
                          align: 'right',
                          render: (val) =>
                            formatCurrency({ value: Number(val), format: 4 }),
                        },
                        {
                          title: 'Thành tiền vàng',
                          dataIndex: 'totalFinalPrice',
                          key: 'totalFinalPrice',
                          align: 'right',
                          render: (val) => formatNumber(val),
                        },
                      ]}
                      summary={() => {
                        const groupList =
                          detailData?.purchaseOrder?.groupPurchaseOrder || [];
                        if (groupList.length === 0) return null;
                        const totalRow =
                          detailData?.purchaseOrder?.totalGroupPurchaseOrder ||
                          groupList.reduce(
                            (acc: any, item: any) => ({
                              totalGoldWeightThread:
                                (acc.totalGoldWeightThread || 0) +
                                (Number(item.totalGoldWeightThread) || 0),
                              totalq10:
                                (acc.totalq10 || 0) +
                                (Number(item.totalq10) || 0),
                              totalq10Accounting:
                                (acc.totalq10Accounting || 0) +
                                (Number(
                                  item.totalq10Accounting ?? item.totalq10,
                                ) || 0),
                              totalFinalPrice:
                                (acc.totalFinalPrice || 0) +
                                (Number(item.totalFinalPrice) || 0),
                            }),
                            {},
                          );
                        return (
                          <Table.Summary.Row
                            style={{
                              backgroundColor: '#fafafa',
                              fontWeight: 'bold',
                            }}
                          >
                            <Table.Summary.Cell index={0} align="center">
                              Tổng
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">
                              {formatCurrency({
                                value: Number(totalRow.totalGoldWeightThread),
                                format: 4,
                              })}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} align="right">
                              {formatCurrency({
                                value: Number(totalRow.totalq10),
                                format: 4,
                              })}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} align="right">
                              {formatCurrency({
                                value: Number(totalRow.totalq10Accounting),
                                format: 4,
                              })}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={4} align="right">
                              {formatNumber(totalRow.totalFinalPrice)}
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        );
                      }}
                    />
                  </div>
                )}
              </Card>

              {/* Panel 4: Tổng cộng (1) + (2) - (3) */}
              <Card
                size="small"
                title={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsTongCongExpanded(!isTongCongExpanded)}
                  >
                    <Space>
                      {isTongCongExpanded ? (
                        <CaretUpOutlined />
                      ) : (
                        <CaretDownOutlined />
                      )}
                      <Text strong style={{ fontSize: 16 }}>
                        Tổng cộng (1) + (2) - (3)
                      </Text>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Tiền tệ: VND
                    </Text>
                  </div>
                }
                style={{ borderRadius: 12, marginBottom: 16 }}
              >
                {isTongCongExpanded && (
                  <div>
                    {/* Summary Math Table */}
                    <Table
                      dataSource={[
                        {
                          key: 'summary',
                          q10: combinedQ10,
                          haolai: combinedHaolai,
                          q10tong: combinedQ10Tong,
                          unitPrice: combinedUnitPrice,
                          processPrice: combinedProcessPrice,
                          goldPrice: combinedGoldPrice,
                          totalTT: combinedTotalTT,
                        },
                      ]}
                      pagination={false}
                      size="small"
                      bordered
                      columns={[
                        {
                          title: 'Q10 (chỉ)',
                          dataIndex: 'q10',
                          align: 'right',
                          render: (val) =>
                            formatCurrency({ value: Number(val), format: 4 }),
                        },
                        {
                          title: 'Hao lai q10(chỉ)',
                          dataIndex: 'haolai',
                          align: 'right',
                          render: (val) =>
                            formatCurrency({ value: Number(val), format: 4 }),
                        },
                        {
                          title: 'Q10 tổng',
                          dataIndex: 'q10tong',
                          align: 'right',
                          render: (val) =>
                            formatCurrency({ value: Number(val), format: 4 }),
                        },
                        {
                          title: 'TT Món',
                          dataIndex: 'unitPrice',
                          align: 'right',
                          render: (val) => formatNumber(val || 0),
                        },
                        {
                          title: 'TT Công',
                          dataIndex: 'processPrice',
                          align: 'right',
                          render: (val) => formatNumber(val || 0),
                        },
                        {
                          title: 'TT vàng',
                          dataIndex: 'goldPrice',
                          align: 'right',
                          render: (val) => formatNumber(val || 0),
                        },
                        {
                          title: 'Tổng TT',
                          dataIndex: 'totalTT',
                          align: 'right',
                          render: (val) => (
                            <Text strong style={{ color: '#1890ff' }}>
                              {formatNumber(val)}
                            </Text>
                          ),
                        },
                      ]}
                    />

                    {/* Double Row: PNJP nợ / Khách hàng nợ */}
                    <Row gutter={16} style={{ marginTop: 16 }}>
                      <Col span={12}>
                        <Form.Item label="PNJP nợ">
                          <Input
                            readOnly
                            value={formatNumber(pnjpNoVal)}
                            addonAfter="VND"
                            style={{ backgroundColor: '#f5f5f5' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Khách hàng nợ">
                          <Input
                            readOnly
                            value={formatNumber(khachNoVal)}
                            addonAfter="VND"
                            style={{ backgroundColor: '#f5f5f5' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Text strong style={{ fontSize: 16, color: '#002140' }}>
                      Phương thức thanh toán
                    </Text>

                    <Row gutter={12} style={{ marginTop: 12 }}>
                      <Col span={8}>
                        <Form.Item label="Chuyển khoản">
                          <Input
                            readOnly
                            value={formatNumber(chuyenKhoanVal)}
                            addonAfter="VND"
                            style={{ backgroundColor: '#f5f5f5' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label="Số tiền thực tế chuyển khoản">
                          <InputNumber
                            style={{ width: '100%' }}
                            disabled={hasActiveQr}
                            value={thucTeTransfer}
                            formatter={(value) =>
                              value
                                ? `${value}`.replace(
                                    /\B(?=(\d{3})+(?!\d))/g,
                                    '.',
                                  )
                                : ''
                            }
                            parser={(value) =>
                              value ? Number(value.replace(/\./g, '')) : 0
                            }
                            onChange={(val) => {
                              const numVal = Number(val) || 0;
                              setThucTeTransfer(numVal);
                              setIsUserEditedTransfer(true);
                              setIsDirty(true);
                            }}
                            addonAfter="VND"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label="Còn lại">
                          <Input
                            readOnly
                            value={formatNumber(conLaiVal)}
                            addonAfter="VND"
                            style={{ backgroundColor: '#f5f5f5' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      name="totalNote"
                      label="Nội dung thanh toán"
                      style={{ marginTop: 8 }}
                    >
                      <Input.TextArea
                        rows={2}
                        placeholder="Nội dung thanh toán"
                        onChange={() => setIsDirty(true)}
                      />
                    </Form.Item>

                    <Form.Item
                      name="note"
                      label="Ghi chú"
                      style={{ marginTop: 8 }}
                    >
                      <Input.TextArea
                        rows={2}
                        placeholder="Ghi chú"
                        onChange={() => setIsDirty(true)}
                      />
                    </Form.Item>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </Form>

        {/* Modal 1: Choose Sale Orders Dialog */}
        <Modal
          title="Chọn bảng kê bán hàng"
          open={isSelectSalesModalOpen}
          onOk={handleConfirmSelectSales}
          onCancel={() => setIsSelectSalesModalOpen(false)}
          okText="Hoàn tất"
          cancelText="Hủy"
        >
          <div style={{ maxHeight: 350, overflowY: 'auto' }}>
            {saleOrderList.length === 0 ? (
              <Text type="secondary">Không có bảng kê nào khả dụng</Text>
            ) : (
              saleOrderList.map((item, idx) => {
                const orderId =
                  typeof item === 'string'
                    ? item
                    : item?.orderId || item?.order_id || item?.id || '';
                const isChecked = tempSelectedSales.includes(orderId);
                return (
                  <div
                    key={orderId || idx}
                    style={{
                      padding: '8px 0',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <Checkbox
                      checked={isChecked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTempSelectedSales((prev) => [...prev, orderId]);
                        } else {
                          setTempSelectedSales((prev) =>
                            prev.filter((id) => id !== orderId),
                          );
                        }
                      }}
                    >
                      <Text strong style={{ color: '#1890ff' }}>
                        {orderId}
                      </Text>
                      {(item?.customerName ||
                        item?.customer_name ||
                        item?.sapCustomerId ||
                        item?.sap_customer_id) && (
                        <Text
                          type="secondary"
                          style={{ marginLeft: 8, fontSize: 13 }}
                        >
                          (
                          {item?.sapCustomerId ||
                            item?.sap_customer_id ||
                            item?.customerId ||
                            item?.customer_id}
                          {' - '}
                          {item?.customerStoreName ||
                            item?.customer_store_name ||
                            item?.customerName ||
                            item?.customer_name}
                          )
                        </Text>
                      )}
                    </Checkbox>
                  </div>
                );
              })
            )}
          </div>
        </Modal>

        {/* Modal 2: Choose Purchase Orders Dialog */}
        <Modal
          title="Chọn bảng dẻ mua hàng"
          open={isSelectPurchasesModalOpen}
          onOk={handleConfirmSelectPurchases}
          onCancel={() => setIsSelectPurchasesModalOpen(false)}
          okText="Hoàn tất"
          cancelText="Hủy"
        >
          <div style={{ maxHeight: 350, overflowY: 'auto' }}>
            {purchaseOrderList.length === 0 ? (
              <Text type="secondary">Không có bảng dẻ nào khả dụng</Text>
            ) : (
              purchaseOrderList.map((item, idx) => {
                const orderId =
                  typeof item === 'string'
                    ? item
                    : item?.orderId || item?.order_id || item?.id || '';
                const isChecked = tempSelectedPurchases.includes(orderId);
                return (
                  <div
                    key={orderId || idx}
                    style={{
                      padding: '8px 0',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <Checkbox
                      checked={isChecked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTempSelectedPurchases((prev) => [
                            ...prev,
                            orderId,
                          ]);
                        } else {
                          setTempSelectedPurchases((prev) =>
                            prev.filter((id) => id !== orderId),
                          );
                        }
                      }}
                    >
                      <Text strong style={{ color: '#1890ff' }}>
                        {orderId}
                      </Text>
                    </Checkbox>
                  </div>
                );
              })
            )}
          </div>
        </Modal>

        {/* Modal 3: Bảng chi tiết thanh toán (matching Mobile V2) */}
        <Modal
          title={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingRight: 24,
              }}
            >
              <span style={{ fontSize: 18, fontWeight: 700, color: '#1f2937' }}>
                Bảng chi tiết thanh toán
              </span>
              {isFullyPaid && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={sendingSapFull}
                  disabled={
                    paymentDetailsLines.length > 0 &&
                    paymentDetailsLines.every(
                      (line: any) =>
                        line.sapStatus === 'SUCCESS' ||
                        line.sapStatus === 'Thành công' ||
                        line.sapMessage === 'Thành công',
                    )
                  }
                  onClick={handleSendSapFull}
                  style={{
                    backgroundColor:
                      paymentDetailsLines.length > 0 &&
                      paymentDetailsLines.every(
                        (line: any) =>
                          line.sapStatus === 'SUCCESS' ||
                          line.sapStatus === 'Thành công' ||
                          line.sapMessage === 'Thành công',
                      )
                        ? undefined
                        : '#52c41a',
                    borderColor:
                      paymentDetailsLines.length > 0 &&
                      paymentDetailsLines.every(
                        (line: any) =>
                          line.sapStatus === 'SUCCESS' ||
                          line.sapStatus === 'Thành công' ||
                          line.sapMessage === 'Thành công',
                      )
                        ? undefined
                        : '#52c41a',
                    borderRadius: 6,
                    fontWeight: 600,
                  }}
                >
                  Gửi lại (Toàn phần)
                </Button>
              )}
            </div>
          }
          open={isPaymentDetailsModalOpen}
          onCancel={() => setIsPaymentDetailsModalOpen(false)}
          footer={[
            <Button
              key="close"
              onClick={() => setIsPaymentDetailsModalOpen(false)}
            >
              Đóng
            </Button>,
          ]}
          width={1380}
        >
          <Table
            dataSource={paymentDetailsLines}
            loading={paymentDetailsLoading}
            rowKey={(record, idx) =>
              record.orderId || record.billingCode || idx
            }
            pagination={false}
            size="small"
            bordered
            scroll={{ x: 1350 }}
            summary={(pageData) => {
              let totalNeed = 0;
              pageData.forEach((row: any) => {
                totalNeed += Number(row.totalAmount || 0);
              });
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row
                    style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text style={{ fontWeight: 700 }}>Tổng</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text style={{ fontWeight: 700 }}>
                        {formatNumber(totalNeed)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} />
                    <Table.Summary.Cell index={5} />
                    <Table.Summary.Cell index={6} />
                    <Table.Summary.Cell index={7} />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
            columns={[
              {
                title: 'Billing',
                dataIndex: 'billingCode',
                key: 'billingCode',
                width: 130,
                render: (val) => val || '-',
              },
              {
                title: 'Bảng kê',
                dataIndex: 'orderId',
                key: 'orderId',
                width: 170,
                render: (val) => <Text strong>{val || '-'}</Text>,
              },
              {
                title: 'QR thanh toán',
                key: 'allocatedQrs',
                width: 220,
                render: (_, record: any) => {
                  if (
                    !record.allocatedQrs ||
                    record.allocatedQrs.length === 0
                  ) {
                    return '-';
                  }
                  return (
                    <div>
                      {record.allocatedQrs.map((qr: any, i: number) => (
                        <div
                          key={i}
                          style={{
                            fontSize: 11,
                            color: '#096dd9',
                            fontWeight: 600,
                          }}
                        >
                          QR {qr.txnId}: +{formatNumber(qr.amount)}đ
                        </div>
                      ))}
                    </div>
                  );
                },
              },
              {
                title: 'Tổng cần TT',
                dataIndex: 'totalAmount',
                key: 'totalAmount',
                width: 160,
                align: 'right',
                render: (val) => formatNumber(val || 0),
              },
              {
                title: 'Trạng thái TT',
                dataIndex: 'paymentStatus',
                key: 'paymentStatus',
                width: 190,
                align: 'center',
                render: (val, record: any) => {
                  let statusStr = val;
                  if (
                    record.type === 'Debt' &&
                    !record.isAutoOffset &&
                    (!val || val === 'Chưa thanh toán')
                  ) {
                    statusStr = 'Công nợ';
                  }
                  if (!statusStr) statusStr = 'Chưa thanh toán';

                  let color = 'default';
                  if (
                    statusStr === 'Thành Công' ||
                    statusStr === 'Đã thanh toán'
                  ) {
                    color = 'success';
                  } else if (statusStr === 'Thanh toán 1 phần') {
                    color = 'warning';
                  } else if (statusStr === 'Thất Bại') {
                    color = 'error';
                  } else if (statusStr === 'Công nợ') {
                    color = 'default';
                  }
                  return <Tag color={color}>{statusStr}</Tag>;
                },
              },
              {
                title: 'Trạng thái SAP',
                dataIndex: 'sapStatus',
                key: 'sapStatus',
                width: 130,
                align: 'center',
                render: (val, record: any) => {
                  if (
                    record.type === 'Debt' &&
                    !record.isAutoOffset &&
                    (!val || val === 'PENDING')
                  ) {
                    return '-';
                  }
                  const st = val || 'PENDING';
                  const msg = record?.sapMessage || '';

                  if (st === 'SUCCESS')
                    return <Tag color="success">Thành công</Tag>;
                  if (st === 'PARTIAL_SUCCESS' || st === 'PARTIAL')
                    return <Tag color="warning">Đã gửi SAP</Tag>;
                  if (st === 'FAILED') return <Tag color="error">Thất bại</Tag>;
                  if (msg && msg !== '-')
                    return <Tag color="processing">Đã gửi SAP</Tag>;
                  return <Tag color="default">Chưa gửi</Tag>;
                },
              },
              {
                title: 'Nguyên nhân SAP',
                dataIndex: 'sapMessage',
                key: 'sapMessage',
                width: 250,
                render: (val) => {
                  if (!val) return '-';
                  if (val === 'Thành công') {
                    return <Text style={{ color: '#52c41a' }}>Thành công</Text>;
                  }
                  return <Text type="danger">{val}</Text>;
                },
              },
              {
                title: 'Gửi lại',
                key: 'actions',
                fixed: 'right',
                width: 110,
                align: 'center',
                render: (_, record: any) => {
                  const type = record.type || '';
                  const paymentStatusStr = record.paymentStatus || '';
                  const isSapSuccess =
                    record.sapStatus === 'SUCCESS' ||
                    record.sapStatus === 'PARTIAL_SUCCESS' ||
                    record.sapStatus === 'Thành công';

                  const showRowResend =
                    !isFullyPaid &&
                    !isSapSuccess &&
                    (paymentStatusStr === 'Thành Công' ||
                      paymentStatusStr === 'Thanh toán 1 phần' ||
                      paymentStatusStr === 'Đã ứng trước') &&
                    (type === 'Sale' || type === 'Debt');

                  if (!showRowResend) return null;

                  return (
                    <Button
                      type="primary"
                      size="small"
                      icon={<SendOutlined />}
                      onClick={() => handleSendSapPartial(record)}
                      style={{
                        borderRadius: 4,
                        fontSize: 12,
                      }}
                    >
                      Gửi lại
                    </Button>
                  );
                },
              },
            ]}
          />
        </Modal>

        {/* Modal 4: Dynamic QR Generator / Viewer (matching Mobile V2 100%) */}
        <Modal
          title="Tạo / Xem QR thanh toán"
          open={isQrModalOpen}
          onCancel={() => setIsQrModalOpen(false)}
          width={750}
          footer={null}
        >
          <Spin spinning={isQrModalLoading} tip="Đang xử lý mã QR...">
            <div>
              {/* Info Header Row */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                  padding: '10px 14px',
                  backgroundColor: '#f8fafc',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                }}
              >
                <Text strong style={{ fontSize: 14 }}>
                  Mã THTT:{' '}
                  <Text style={{ color: '#0052cc' }}>
                    {sumPaymentId || 'Chưa tạo'}
                  </Text>
                </Text>
                <Text strong style={{ fontSize: 14, color: '#0052cc' }}>
                  Tổng số tiền: {formatNumber(qrAmount)} VNĐ
                </Text>
              </div>

              {/* Split Payment Section */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 20,
                }}
              >
                <Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                  Nhập số tiền cần tách:
                </Text>
                <InputNumber
                  style={{ flex: 1 }}
                  value={splitAmountInput}
                  formatter={(value) =>
                    value
                      ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                      : ''
                  }
                  parser={(value) =>
                    value ? Number(value.replace(/\./g, '')) : 0
                  }
                  onChange={(val) => setSplitAmountInput(Number(val) || 0)}
                  placeholder="Nhập số tiền tách"
                />
                <Button
                  type="primary"
                  loading={isQrModalLoading}
                  disabled={hasPaidQr}
                  style={{
                    backgroundColor: hasPaidQr ? undefined : '#0F2D59',
                    borderColor: hasPaidQr ? undefined : '#0F2D59',
                  }}
                  onClick={() => handleGenerateQrs(qrAmount, splitAmountInput)}
                >
                  Tách thanh toán
                </Button>
                {generatedQrs.length > 0 && (
                  <Button
                    type="primary"
                    danger
                    loading={isQrModalLoading}
                    disabled={hasPaidQr}
                    onClick={handleCancelAllQrs}
                  >
                    Xóa QR / Hủy tách
                  </Button>
                )}
              </div>

              {/* Active & Cancelled QR Lists (matching Mobile V2 100%) */}
              {generatedQrs.length > 0 ? (
                <div>
                  {/* Active QRs Section */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                      maxHeight: 350,
                      overflowY: 'auto',
                      paddingRight: 4,
                    }}
                  >
                    {activeQrs.length === 0 ? (
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '20px 0',
                          color: '#8c8c8c',
                          fontStyle: 'italic',
                        }}
                      >
                        Không có mã QR hoạt động.
                      </div>
                    ) : (
                      activeQrs.map((qr, idx) => {
                        const statusStr = (qr.status || 'PENDING')
                          .toString()
                          .toUpperCase();
                        const isPaid =
                          statusStr === 'SUCCESS' || statusStr === 'PAID';
                        const isFailure =
                          statusStr === 'FAILED' || statusStr === 'FAILURE';

                        let statusLabel = 'Chưa thanh toán';
                        let statusColor = '#fa8c16';
                        if (isPaid) {
                          statusLabel = 'Đã thanh toán';
                          statusColor = '#52c41a';
                        } else if (isFailure) {
                          statusLabel = 'Lỗi thanh toán';
                          statusColor = '#ff4d4f';
                        }

                        const qrImgSrc =
                          qr.image &&
                          (qr.image.startsWith('data:image') ||
                            qr.image.startsWith('http'))
                            ? qr.image
                            : qr.image
                              ? `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qr.image)}`
                              : `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qr.txnId || 'PNJ_PAYMENT')}`;

                        return (
                          <div
                            key={idx}
                            style={{
                              padding: 16,
                              border: '1px solid #e2e8f0',
                              borderRadius: 12,
                              backgroundColor: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 20,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                            }}
                          >
                            {/* Left: QR Image */}
                            <div
                              style={{
                                width: 140,
                                height: 140,
                                borderRadius: 8,
                                border: '1px solid #cbd5e1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#fafafa',
                                flexShrink: 0,
                                overflow: 'hidden',
                              }}
                            >
                              <Image
                                width={130}
                                height={130}
                                style={{ objectFit: 'contain' }}
                                src={qrImgSrc}
                              />
                            </div>

                            {/* Middle: Details */}
                            <div style={{ flex: 1 }}>
                              {activeQrs.length > 1 && (
                                <div
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 'bold',
                                    color: '#0052cc',
                                    marginBottom: 6,
                                  }}
                                >
                                  MÃ QR {idx + 1} / {activeQrs.length}
                                </div>
                              )}
                              <div
                                style={{
                                  display: 'flex',
                                  marginBottom: 6,
                                  fontSize: 13,
                                }}
                              >
                                <span style={{ color: '#64748b', width: 110 }}>
                                  Mã thanh toán:
                                </span>
                                <span
                                  style={{ fontWeight: 600, color: '#1e293b' }}
                                >
                                  {qr.txnId || '-'}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  marginBottom: 6,
                                  fontSize: 13,
                                }}
                              >
                                <span style={{ color: '#64748b', width: 110 }}>
                                  Tổng số tiền:
                                </span>
                                <span
                                  style={{
                                    fontWeight: 'bold',
                                    color: '#0052cc',
                                  }}
                                >
                                  {formatNumber(qr.amount)} VNĐ
                                </span>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  marginBottom: 6,
                                  fontSize: 13,
                                }}
                              >
                                <span style={{ color: '#64748b', width: 110 }}>
                                  Trạng thái:
                                </span>
                                <span
                                  style={{
                                    fontWeight: 'bold',
                                    color: statusColor,
                                  }}
                                >
                                  {statusLabel}
                                </span>
                              </div>
                              <div style={{ display: 'flex', fontSize: 13 }}>
                                <span style={{ color: '#64748b', width: 110 }}>
                                  Nội dung:
                                </span>
                                <span style={{ color: '#334155' }}>
                                  {qr.transactionLogId || qr.note || '-'}
                                </span>
                              </div>
                            </div>

                            {/* Right: Actions */}
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                                alignItems: 'flex-end',
                              }}
                            >
                              <Button
                                style={{
                                  backgroundColor: '#0A3B5C',
                                  borderColor: '#0A3B5C',
                                  color: '#ffffff',
                                  borderRadius: 8,
                                  fontSize: 13,
                                }}
                                onClick={() => setSelectedQrDetail(qr)}
                              >
                                Xem chi tiết
                              </Button>
                              {isFailure && (
                                <Button
                                  style={{
                                    backgroundColor: '#ff9800',
                                    borderColor: '#ff9800',
                                    color: '#ffffff',
                                    borderRadius: 8,
                                    fontSize: 13,
                                  }}
                                  onClick={() =>
                                    handleGenerateQrs(qr.amount, 0)
                                  }
                                >
                                  Tạo lại QR
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Cancelled QRs Section (Lịch sử mã QR đã hủy / thay thế) */}
                  {cancelledQrs.length > 0 && (
                    <div style={{ marginTop: 20 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderTop: '1px solid #e2e8f0',
                          paddingTop: 12,
                        }}
                      >
                        <Text strong style={{ color: '#8c8c8c', fontSize: 13 }}>
                          Lịch sử mã QR đã hủy / thay thế ({cancelledQrs.length}
                          )
                        </Text>
                        <Button
                          type="link"
                          size="small"
                          style={{ color: '#8c8c8c' }}
                          onClick={() =>
                            setIsHistoryExpanded(!isHistoryExpanded)
                          }
                        >
                          {isHistoryExpanded ? (
                            <Space>
                              <span>Thu gọn</span>
                              <CaretUpOutlined />
                            </Space>
                          ) : (
                            <Space>
                              <span>Xem thêm</span>
                              <CaretDownOutlined />
                            </Space>
                          )}
                        </Button>
                      </div>

                      {isHistoryExpanded && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                            marginTop: 12,
                          }}
                        >
                          {cancelledQrs.map((qr, idx) => {
                            const qrImgSrc =
                              qr.image &&
                              (qr.image.startsWith('data:image') ||
                                qr.image.startsWith('http'))
                                ? qr.image
                                : qr.image
                                  ? `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qr.image)}`
                                  : `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qr.txnId || 'PNJ_CANCELLED')}`;

                            return (
                              <div
                                key={idx}
                                style={{
                                  opacity: 0.45,
                                  padding: 16,
                                  border: '1px solid #e2e8f0',
                                  borderRadius: 12,
                                  backgroundColor: '#f8fafc',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 20,
                                }}
                              >
                                <div
                                  style={{
                                    width: 140,
                                    height: 140,
                                    borderRadius: 8,
                                    border: '1px solid #cbd5e1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#fafafa',
                                    flexShrink: 0,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Image
                                    width={130}
                                    height={130}
                                    style={{ objectFit: 'contain' }}
                                    src={qrImgSrc}
                                  />
                                </div>

                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      marginBottom: 6,
                                      fontSize: 13,
                                    }}
                                  >
                                    <span
                                      style={{ color: '#64748b', width: 110 }}
                                    >
                                      Mã thanh toán:
                                    </span>
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color: '#1e293b',
                                      }}
                                    >
                                      {qr.txnId || '-'}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      marginBottom: 6,
                                      fontSize: 13,
                                    }}
                                  >
                                    <span
                                      style={{ color: '#64748b', width: 110 }}
                                    >
                                      Tổng số tiền:
                                    </span>
                                    <span style={{ fontWeight: 'bold' }}>
                                      {formatNumber(qr.amount)} VNĐ
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      marginBottom: 6,
                                      fontSize: 13,
                                    }}
                                  >
                                    <span
                                      style={{ color: '#64748b', width: 110 }}
                                    >
                                      Trạng thái:
                                    </span>
                                    <span
                                      style={{
                                        fontWeight: 'bold',
                                        color: '#8c8c8c',
                                      }}
                                    >
                                      Đã hủy / vô hiệu hóa
                                    </span>
                                  </div>
                                  <div
                                    style={{ display: 'flex', fontSize: 13 }}
                                  >
                                    <span
                                      style={{ color: '#64748b', width: 110 }}
                                    >
                                      Nội dung:
                                    </span>
                                    <span style={{ color: '#334155' }}>
                                      {qr.transactionLogId || qr.note || '-'}
                                    </span>
                                  </div>
                                </div>

                                <div>
                                  <Button disabled style={{ borderRadius: 8 }}>
                                    Đã hủy
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '30px 0',
                    color: '#595959',
                  }}
                >
                  Đang khởi tạo mã QR động từ ngân hàng VCB...
                </div>
              )}
            </div>
          </Spin>
        </Modal>

        {/* Modal 4.1: Chi tiết thanh toán QR Modal (matching Mobile V2 100%) */}
        <Modal
          title="Chi tiết thanh toán"
          open={!!selectedQrDetail}
          onCancel={() => setSelectedQrDetail(null)}
          width={450}
          footer={[
            <Button key="close" onClick={() => setSelectedQrDetail(null)}>
              Đóng
            </Button>,
            <Button
              key="copy"
              type="primary"
              onClick={() => {
                const text = `Mã THTT: ${sumPaymentId}\nMã thanh toán: ${selectedQrDetail?.txnId}\nTổng tiền: ${formatNumber(selectedQrDetail?.amount)} VNĐ\nNội dung: ${selectedQrDetail?.transactionLogId}`;
                navigator.clipboard.writeText(text);
                message.success('Đã sao chép thông tin thanh toán!');
              }}
            >
              Copy thông tin
            </Button>,
          ]}
        >
          {selectedQrDetail && (
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <div
                style={{
                  width: 220,
                  height: 220,
                  margin: '0 auto 16px auto',
                  padding: 10,
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Image
                  width={200}
                  height={200}
                  style={{ objectFit: 'contain' }}
                  src={
                    selectedQrDetail.image &&
                    (selectedQrDetail.image.startsWith('data:image') ||
                      selectedQrDetail.image.startsWith('http'))
                      ? selectedQrDetail.image
                      : `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(selectedQrDetail.image || selectedQrDetail.txnId || 'PNJ')}`
                  }
                />
              </div>

              <div
                style={{
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', fontSize: 14 }}>
                  <span style={{ color: '#64748b', width: 130 }}>Mã THTT:</span>
                  <span style={{ fontWeight: 600, color: '#0052cc' }}>
                    {sumPaymentId || '-'}
                  </span>
                </div>
                <div style={{ display: 'flex', fontSize: 14 }}>
                  <span style={{ color: '#64748b', width: 130 }}>
                    Mã thanh toán:
                  </span>
                  <span style={{ fontWeight: 600 }}>
                    {selectedQrDetail.txnId || '-'}
                  </span>
                </div>
                <div style={{ display: 'flex', fontSize: 14 }}>
                  <span style={{ color: '#64748b', width: 130 }}>
                    Tổng số tiền:
                  </span>
                  <span style={{ fontWeight: 'bold', color: '#0052cc' }}>
                    {formatNumber(selectedQrDetail.amount)} VNĐ
                  </span>
                </div>
                <div style={{ display: 'flex', fontSize: 14 }}>
                  <span style={{ color: '#64748b', width: 130 }}>
                    Trạng thái:
                  </span>
                  <span
                    style={{
                      fontWeight: 'bold',
                      color:
                        selectedQrDetail.status === 'PAID' ||
                        selectedQrDetail.status === 'SUCCESS'
                          ? '#52c41a'
                          : '#fa8c16',
                    }}
                  >
                    {selectedQrDetail.status === 'PAID' ||
                    selectedQrDetail.status === 'SUCCESS'
                      ? 'Đã thanh toán'
                      : 'Chưa thanh toán'}
                  </span>
                </div>
                <div style={{ display: 'flex', fontSize: 14 }}>
                  <span style={{ color: '#64748b', width: 130 }}>
                    Nội dung:
                  </span>
                  <span>
                    {selectedQrDetail.transactionLogId ||
                      selectedQrDetail.note ||
                      '-'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* Modal 5: Einvoice Status Dialog (Tạo einvoice - matching Mobile V2) */}
        <Modal
          title="Tạo einvoice"
          open={isHddtModalOpen}
          onCancel={() => setIsHddtModalOpen(false)}
          footer={[
            <Button key="close" onClick={() => setIsHddtModalOpen(false)}>
              Đóng
            </Button>,
          ]}
          width={950}
        >
          <Table
            dataSource={hddtOrders}
            rowKey="orderId"
            pagination={false}
            size="small"
            bordered
            scroll={{ x: 800 }}
            columns={[
              {
                title: 'Bảng kê',
                dataIndex: 'orderId',
                key: 'orderId',
                render: (val) => <Text strong>{val || '-'}</Text>,
              },
              {
                title: 'SO',
                dataIndex: 'soId',
                key: 'soId',
                render: (val) => val || '-',
              },
              {
                title: 'Trạng thái đơn',
                dataIndex: 'status',
                key: 'status',
                render: (val) => (
                  <Text style={{ color: '#52c41a', fontWeight: 500 }}>
                    {val || '-'}
                  </Text>
                ),
              },
              {
                title: 'Thông báo lỗi',
                dataIndex: 'errorMessage',
                key: 'errorMessage',
                render: (val) => (val ? <Text type="danger">{val}</Text> : '-'),
              },
              {
                title: 'DO',
                dataIndex: 'doId',
                key: 'doId',
                render: (val) => val || '-',
              },
              {
                title: 'GI',
                dataIndex: 'giId',
                key: 'giId',
                render: (val) => val || '-',
              },
              {
                title: 'BILLING',
                dataIndex: 'billingId',
                key: 'billingId',
                render: (val) => val || '-',
              },
              {
                title: 'Einvoice',
                dataIndex: 'envoiceId',
                key: 'envoiceId',
                render: (val) => val || '-',
              },
              {
                title: 'Link',
                dataIndex: 'link',
                key: 'link',
                render: (val) =>
                  val ? (
                    <a
                      href={val}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#1890ff', fontWeight: 500 }}
                    >
                      Xem HĐĐT
                    </a>
                  ) : (
                    '-'
                  ),
              },
              {
                title: 'Actions',
                key: 'actions',
                fixed: 'right',
                width: 220,
                render: (_, item: any) => {
                  const canCreateBilling =
                    (!item.link || item.link.length === 0) &&
                    item.giId &&
                    item.giId.length > 0;
                  const hasBilling =
                    item.billingId && item.billingId.length > 0;
                  const canCreateInvoice =
                    (!item.link || item.link.length === 0) &&
                    hasBilling &&
                    (!item.envoiceId || item.envoiceId.length === 0);
                  const hasLink = !!(item.link && item.link.length > 0);
                  const canResend = hasLink;

                  const resendMenuItems: MenuProps['items'] = [
                    {
                      key: 'resend_email',
                      icon: <MailOutlined style={{ color: '#1890ff' }} />,
                      label: 'Gửi lại Email',
                      onClick: () => handleResendEinvoice(item, 'EMAIL'),
                    },
                    {
                      key: 'resend_sms',
                      icon: <MessageOutlined style={{ color: '#52c41a' }} />,
                      label: 'Gửi lại SMS',
                      onClick: () => handleResendEinvoice(item, 'SMS'),
                    },
                    {
                      key: 'resend_all',
                      icon: <SendOutlined style={{ color: '#1890ff' }} />,
                      label: 'Gửi lại tất cả (Email & SMS)',
                      onClick: () => handleResendEinvoice(item, 'ALL'),
                    },
                  ];

                  return (
                    <Space size={2}>
                      <Tooltip title="Xem bảng kê">
                        <Button
                          type="text"
                          size="small"
                          icon={<EyeOutlined style={{ color: '#1890ff' }} />}
                          onClick={() => {
                            if (item.orderId) {
                              window.open(
                                `/operation/cn-follow-plan/detail?id=${item.orderId}`,
                                '_blank',
                              );
                            }
                          }}
                        />
                      </Tooltip>

                      <Tooltip title="Tạo BILLING">
                        <Button
                          type="text"
                          size="small"
                          icon={
                            <FileTextOutlined
                              style={{
                                color: canCreateBilling ? '#1890ff' : '#bfbfbf',
                              }}
                            />
                          }
                          disabled={!canCreateBilling}
                          onClick={() => handleCreateBilling(item)}
                        />
                      </Tooltip>

                      <Tooltip title="Tạo hóa đơn điện tử">
                        <Button
                          type="text"
                          size="small"
                          icon={
                            <FileAddOutlined
                              style={{
                                color: canCreateInvoice ? '#1890ff' : '#bfbfbf',
                              }}
                            />
                          }
                          disabled={!canCreateInvoice}
                          onClick={() => handleCreateEinvoice(item)}
                        />
                      </Tooltip>

                      <Tooltip title="Gửi lại HĐĐT (Email / SMS)">
                        <Dropdown
                          menu={{ items: resendMenuItems }}
                          trigger={['click']}
                          disabled={!canResend}
                        >
                          <Button
                            type="text"
                            size="small"
                            icon={
                              <SendOutlined
                                style={{
                                  color: canResend ? '#1890ff' : '#bfbfbf',
                                }}
                              />
                            }
                            disabled={!canResend}
                          />
                        </Dropdown>
                      </Tooltip>

                      <Tooltip title="In hóa đơn">
                        <Button
                          type="text"
                          size="small"
                          icon={
                            <PrinterOutlined
                              style={{
                                color: hasLink ? '#1890ff' : '#bfbfbf',
                              }}
                            />
                          }
                          disabled={!hasLink}
                          onClick={() => window.open(item.link, '_blank')}
                        />
                      </Tooltip>

                      <Tooltip title="Copy link">
                        <Button
                          type="text"
                          size="small"
                          icon={
                            <CopyOutlined
                              style={{
                                color: hasLink ? '#1890ff' : '#bfbfbf',
                              }}
                            />
                          }
                          disabled={!hasLink}
                          onClick={() => {
                            navigator.clipboard.writeText(item.link);
                            message.success('Đã copy link HĐĐT!');
                          }}
                        />
                      </Tooltip>
                    </Space>
                  );
                },
              },
            ]}
          />
        </Modal>

        {/* Modal 6: Lịch sử gửi HĐĐT của bảng THTT */}
        <PaymentSummaryEinvoiceHistoryModal
          isOpen={isEinvoiceHistoryModalOpen}
          onClose={() => {
            setIsEinvoiceHistoryModalOpen(false);
            if (sumPaymentId) fetchEinvoiceSummary(sumPaymentId);
          }}
          sumPaymentId={sumPaymentId}
          soCodes={allThttSoCodes}
          customerInfo={detailData?.tcustomer}
        />
      </div>
    </Spin>
  );
};

export default DetailPaymentSummaryPageV2;
